import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, Loader2, ShieldCheck, CheckCircle2,
  AlertTriangle, MapPin, Truck, Zap, Info, MessageCircle, Instagram,
  ClipboardList, Wand2, Star, UserPlus,
  Copy, Check, RotateCcw, HelpCircle, Square, CheckSquare,
} from "lucide-react";
import SEO from "@/components/SEO";
import QuoteCategorySummary, { groupLinesByCategory } from "@/components/QuoteCategorySummary";
import { formatApiError } from "@/lib/api";
import api from "@/lib/api";
import { formatQuoteForClipboard } from "@/lib/formatQuoteForClipboard";
import { buildSoldOutPayload } from "@/lib/soldOutList";
import { formatRelative, formatAbsoluteLocal } from "@/lib/relativeTime";
import { useCollectorAuth } from "@/context/CollectorAuthContext";
import { track, EVENT } from "@/lib/analytics";
import { trackPixelEvent } from "@/lib/metaPixel";
import AbandonmentFeedback from "@/components/AbandonmentFeedback";
import EmailTypoSuggestionDialog from "@/components/EmailTypoSuggestionDialog";
import { mergeFreshPrices, validatePricingInvariant } from "@/lib/pricingInvariant";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const ALBUM_SLUG = "panini-fifa-world-cup-2026";
const ALBUM_DISPLAY = "Panini FIFA World Cup 2026";

// Countries currently the hardest to source. Informational only — never
// used to compute or override a price. Master Sticker Database remains
// the sole source of truth for pricing per Msg-892.
const HARDER_TO_SOURCE_COUNTRIES = new Set(["CZE", "BIH", "TUR", "COD", "IRQ"]);

// Every country/category code the Master Sticker Database currently
// carries. Used to distinguish "unknown country" (e.g. RUS) from
// "sticker number not found" (e.g. ARG-99) when classifying unresolved
// entries for the customer. Adding/removing a prefix here is a display
// concern only — pricing still comes exclusively from the spreadsheet.
const KNOWN_MASTER_PREFIXES = new Set([
  // FIFA host + tournament sections
  "FWC", "CC",
  // 48 nations qualified for the 2026 Panini album
  "ARG", "AUS", "AUT", "BEL", "BIH", "BRA", "CAN", "CIV", "COD", "COL",
  "CPV", "CRO", "CUW", "CZE", "ECU", "EGY", "ENG", "ESP", "FRA", "GER",
  "GHA", "HAI", "IRN", "IRQ", "JOR", "JPN", "KOR", "KSA", "MAR", "MEX",
  "NED", "NOR", "NZL", "PAN", "PAR", "POR", "QAT", "RSA", "SCO", "SEN",
  "SUI", "SWE", "TUN", "TUR", "URU", "USA", "UZB", "ALG",
]);

// Category grouping is now sourced from
// `@/components/QuoteCategorySummary`. The 8-section order + labels
// are defined there and driven by the backend `display_category`
// tag on each priced line.

// Local delivery catalogue — three supported options.
// Codes must match the backend `shipping_options` in default_data.py.
const DELIVERY_OPTIONS = [
  { code: "local_pickup", label: "Local Pickup",
    hint: "Your Phone Plug · 20719 NW 2nd Ave, Miami, FL 33160",
    cost: 0 },
  { code: "usps_stamp", label: "USPS Stamp",
    hint: "Estimated delivery: 2–7 business days after shipment",
    cost: 4.00 },
  { code: "ups_ground", label: "UPS Ground",
    hint: "Shipped in a box with tracking. Estimated delivery: 1–2 business days after shipment.",
    cost: 12.00 },
];

// Shipping & Fulfillment · Phase 1 — Florida sales tax applies to every
// order regardless of delivery method (the business ships from FL).
// Never gate this on delivery — see the historic bug where selecting
// UPS Shipping silently dropped the sales-tax line and understated the
// customer's total.
const TAX_JURISDICTION = "US-FL";

const PLACEHOLDER = `MEX: 13
RSA: 4
KOR: 15
BIH: 10
MAR: 7
NED: 1, 10, 18
SWE: 4, 5
IRN: 12
KSA: 1`;


// Iter T · Phase 1 — stable per-browser-session id used to correlate
// multiple Review Quote clicks from the same visitor. Persisted in
// sessionStorage (per-tab lifetime). SSR-safe (crypto guarded).
function quoteSessionId() {
  try {
    const K = "cu.q.session_id";
    let v = window.sessionStorage.getItem(K);
    if (!v) {
      v = (window.crypto?.randomUUID?.() || `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
      window.sessionStorage.setItem(K, v);
    }
    return v;
  } catch (_e) { return null; }
}

// F4 (owner directive 2026-02-20): rotating the session id guarantees
// the next `POST /orders/quote-activity/log` will insert a NEW
// activity row rather than idempotently update the previous row. This
// is called from `setVariantAndReset` whenever the customer intent
// changes the variant selection.
function rotateQuoteSessionId() {
  try {
    const K = "cu.q.session_id";
    const v = (window.crypto?.randomUUID?.() || `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
    window.sessionStorage.setItem(K, v);
    return v;
  } catch (_e) { return null; }
}

export default function MissingStickers() {
  const nav = useNavigate();
  const { collector, loading: authLoading, refresh: refreshAuth } = useCollectorAuth();
  const [step, setStep] = useState(1);
  const [raw, setRaw] = useState("");
  // Meta Pixel — ViewContent fires ONCE per page landing on
  // /order-missing so Meta learns this is the funnel entry. The
  // `metaPixel.js` helper skips subsequent fires for the same
  // pathname within a session (guards against React StrictMode
  // double-mount and Router replace-state churn).
  React.useEffect(() => {
    trackPixelEvent("ViewContent", {
      content_name:     "Missing Stickers Ordering Service",
      content_category: "Panini World Cup 2026 Missing Stickers",
    }, { once: true, eventId: "viewcontent_order-missing" });
  }, []);
  // Quote-to-Order · Frontend Completion — hydrate from a resumed quote
  // when the customer arrived via /order-missing/resume. The paste box
  // is prefilled with the frozen sticker lines so the customer can
  // continue straight into Review Quote without rebuilding the list.
  //
  // Phase Q2 — the magic-link resume flow lands here with `?resume=2`
  // and a much richer session payload (`resumed_quote_v2`) that carries
  // the customer identity + full parsed/quote snapshots. In that case
  // we skip Paste + Contact entirely and drop the user straight on
  // Step 3 REVIEW.
  React.useEffect(() => {
    try {
      const search = new URLSearchParams(window.location.search);
      const kind = search.get("resume");
      if (!kind) return;

      if (kind === "2") {
        const raw = sessionStorage.getItem("resumed_quote_v2");
        if (!raw) return;
        const q = JSON.parse(raw);
        sessionStorage.removeItem("resumed_quote_v2");

        const cust = q.customer || {};
        setCustomer((c) => ({
          ...c,
          first_name: cust.first_name || c.first_name || "",
          last_name:  cust.last_name  || c.last_name  || "",
          name:       cust.name       || `${cust.first_name || ""} ${cust.last_name || ""}`.trim() || c.name,
          email:      cust.email      || c.email || "",
          phone:      cust.phone      || c.phone || "",
        }));
        if (q.parsed) setParsed(q.parsed);
        if (q.quote)  setQuote(q.quote);
        // Rebuild the raw text so an accidental "Back to paste" still
        // shows the customer their list.
        const lines = ((q.parsed || {}).items || [])
          .filter((it) => it && it.code)
          .map((it) => `${it.code}${(it.qty || 1) > 1 ? ` x${it.qty}` : ""}`);
        if (lines.length) setRaw(lines.join(", "));
        if (q.quote_public_id) setSourceQuoteId(q.quote_public_id);
        // Phase Q4 — carry the magic-link token so the feedback UI can
        // authorize its submissions with the same token that opened the
        // resumed quote. Also seed any prior feedback so the form
        // renders the thank-you state instead of a fresh form.
        if (q.resume_token) setResumeToken(q.resume_token);
        if (q.feedback) setExistingFeedback(q.feedback);
        setFeedbackSource("resume_link");
        // Admin Quote Adjustments — hydrate the frozen discount /
        // custom items when the admin edited this quote. Never
        // overwrites `quote_snapshot`; StepReview picks it up as a
        // display-time overlay.
        if (q.admin_adjustments) setAdminAdjustments(q.admin_adjustments);
        if (q.admin_finalized)   setAdminFinalized(true);
        setContactCaptured(true);
        setStep(3);
        return;
      }

      // Legacy resume=1 path (ID + email form) — paste-only rehydrate.
      const raw = sessionStorage.getItem("resumed_quote");
      if (!raw) return;
      const q = JSON.parse(raw);
      const lines = (q.items || [])
        .filter((it) => it && it.code)
        .map((it) => `${it.code}${(it.qty || 1) > 1 ? ` x${it.qty}` : ""}`);
      if (lines.length) setRaw(lines.join(", "));
    } catch { /* ignore */ }
  }, []);
  const [parsed, setParsed] = useState(null);      // { items, unknowns }
  const [quote, setQuote] = useState(null);        // pricing snapshot
  const [delivery, setDelivery] = useState("local_pickup");
  // Blue Parallel · sticker version selector. Defaults to "white"
  // so the existing customer experience is unchanged. Persisted with
  // the order so admin can identify Blue Parallel requests.
  const [variant, setVariant] = useState("white");
  // Phase 4 · Collection selector. Defaults to WC26 so every existing
  // customer flow is unchanged. "russia_2018" routes the paste/quote/
  // submit through the Russia pricing module (variant forced to
  // black_back, album to panini-wc-russia-2018) and swaps the UI into
  // numeric-paste mode. Query param `?collection=russia_2018` on the
  // URL preselects Russia on first load.
  const location = useLocation();
  const [collection, setCollection] = useState(() => {
    try {
      const sp = new URLSearchParams(location?.search || window.location.search);
      return sp.get("collection") === "russia_2018" ? "russia_2018" : "wc2026";
    } catch (_e) { return "wc2026"; }
  });
  const isRussia = collection === "russia_2018";
  const effectiveAlbumSlug = isRussia ? "panini-wc-russia-2018" : ALBUM_SLUG;
  const effectiveVariant = isRussia ? "black_back" : variant;
  // Variant readiness — fetched at mount. If a variant isn't ready,
  // its toggle is disabled and the customer can't select it.
  const [blueReady, setBlueReady] = useState(false);
  const [goldReady, setGoldReady] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/orders/variants/status`);
        if (alive) {
          setBlueReady(Boolean(r.data?.blue?.ready));
          setGoldReady(Boolean(r.data?.gold_crumple?.ready));
        }
      } catch (_) { /* stay on White if the probe fails */ }
    })();
    return () => { alive = false; };
  }, []);
  // Guard: if a customer somehow has a not-ready variant selected,
  // force them back to White so no /orders/quote fires with the
  // blocked variant.
  useEffect(() => {
    if (!blueReady && variant === "blue") setVariant("white");
    if (!goldReady && variant === "gold_crumple") setVariant("white");
  }, [blueReady, goldReady, variant]);
  // F4 (owner directive 2026-02-20) · Changing variant intent
  // invalidates every piece of state that belongs to the previous
  // variant's pricing result AND rotates the quote session id so
  // the next `quote-activity/log` inserts a fresh activity row
  // rather than idempotently updating the previous variant's row.
  // Called from the paste-step variant toggles. Safe to call even
  // when the target variant equals the current variant (no-op).
  const setVariantAndReset = useCallback((next) => {
    if (next === variant) return;
    setVariant(next);
    setParsed(null);
    setQuote(null);
    setOriginalLines([]);
    originalItemsRef.current = [];
    setSourceQuoteId(null);
    setContactRequestId(null);
    setDeselected(new Set());
    quoteLogPromiseRef.current = null;
    rotateQuoteSessionId();
  }, [variant]);
  // Phase 4 · Switching collections must reset every piece of state
  // bound to the previous collection's sticker set so no WC26 line
  // leaks into a Russia quote (or vice-versa). Customer/contact
  // fields intentionally remain so the customer isn't retyping
  // their email/phone when they explore both collections.
  const setCollectionAndReset = useCallback((next) => {
    if (next === collection) return;
    setCollection(next);
    setParsed(null);
    setQuote(null);
    setOriginalLines([]);
    originalItemsRef.current = [];
    setSourceQuoteId(null);
    setContactRequestId(null);
    setDeselected(new Set());
    quoteLogPromiseRef.current = null;
    setRaw("");
    rotateQuoteSessionId();
    // Reset variant to white when returning to WC26 so a stale
    // `black_back` state from the Russia branch never leaks.
    if (next === "wc2026") setVariant("white");
  }, [collection]);
  const [customer, setCustomer] = useState({
    // Missing-Sticker Ordering · Customer Identity Gate — split name
    // into two explicit fields (`name` is kept in the state for
    // downstream telemetry code that hasn't been migrated yet).
    first_name: "", last_name: "",
    name: "", email: "", phone: "", instagram_handle: "", pinned_instructions: "",
    // Phase 3A.1 — Structured shipping address. Only sent for non-pickup orders.
    // Retained locally even if the user flips back to pickup so a
    // subsequent shipping choice doesn't lose data.
    shipping_address: { line1: "", line2: "", city: "", state: "", postal_code: "", country: "United States" } });
  const [checkoutMode, setCheckoutMode] = useState(null); // "guest" | "account"
  const [busy, setBusy] = useState(false);
  // Phase 4 — Per-sticker selection. `deselected` holds the codes the
  // customer has unchecked on the Review Quote screen. Empty by default
  // (every sticker starts selected). `originalItems` is the immutable
  // set the customer originally submitted — used by Restore All and by
  // submit() to build the final item list from the current selection.
  // Everything is client-only; the backend re-quotes authoritatively
  // whenever the effective item list changes.
  const [deselected, setDeselected] = useState(() => new Set());
  const originalItemsRef = useRef([]);
  const [originalLines, setOriginalLines] = useState(null);
  const [selectionBusy, setSelectionBusy] = useState(false);
  // Cross-Variant Pricing-Integrity race guard — every /orders/quote
  // request is stamped with a monotonic sequence. Late responses whose
  // seq is behind the latest are discarded. An AbortController is
  // paired so the network layer also drops the connection when the
  // customer keeps toggling. See lib/pricingInvariant.js for the
  // merge + validation helpers.
  const quoteSeqRef = useRef(0);
  const quoteAbortRef = useRef(null);
  const [pricingIntegrityBlock, setPricingIntegrityBlock] = useState(null);
  // Phase 1c — token for the server-side pending checkout draft. Set when
  // the customer chooses "Create Free Account" AND again if we restore
  // from a `?pc=…` URL after an OAuth or register/login redirect.
  const [pcToken, setPcToken] = useState(null);
  const topRef = useRef(null);
  // Iter T · Phase 1 — Quote Activity capture. Ref holds the promise so
  // submit() can await it briefly if it hasn't resolved yet.
  const quoteLogPromiseRef = useRef(null);
  const [sourceQuoteId, setSourceQuoteId] = useState(null);
  // Phase Q1 — Lead Capture. `contactCaptured` gates the detailed
  // quote reveal. `contactRequestId` is the request_id from the initial
  // parse+quote so the follow-up quote-activity/log call upserts on the
  // same (request_id, session_id) pair. `newsletterOptIn` is optional
  // and defaults false; it never blocks the quote.
  const [contactCaptured, setContactCaptured] = useState(false);
  // Phase 4 · email typo/domain validation UX state.
  const [emailValidation, setEmailValidation] = useState(null);
  const [emailSuggestion, setEmailSuggestion] = useState(null);
  const [showEmailSuggestion, setShowEmailSuggestion] = useState(false);
  const [emailTypoConfirmed, setEmailTypoConfirmed] = useState(false);
  const [contactRequestId, setContactRequestId] = useState(null);
  const [newsletterOptIn, setNewsletterOptIn] = useState(false);
  // Phase Q4 — Q2 resume token (only present when the customer arrived
  // via a magic link) and any feedback the customer has already
  // submitted. Both are optional; the AbandonmentFeedback component
  // decides UI state from them.
  const [resumeToken, setResumeToken] = useState(null);
  const [existingFeedback, setExistingFeedback] = useState(null);
  const [feedbackSource, setFeedbackSource] = useState("website_quote");
  // Admin Quote Adjustments — hydrated from the resume payload
  // (`resumed_quote_v2.admin_adjustments`). When populated AND the
  // quote is admin-finalized, StepReview renders an "Admin Discount"
  // line + uses `revised_totals.final_total` for the grand total.
  // Read-only from the customer perspective. Server-authoritative on
  // submit — the /orders convert path replays the same math from the
  // stored `finalization.snapshot` so the customer can never mutate.
  const [adminAdjustments, setAdminAdjustments] = useState(null);
  const [adminFinalized,   setAdminFinalized]   = useState(false);

  // Phase 1c — Restore-from-pending-checkout. If the URL carries `?pc=…`
  // we skip Step 1/2/3, hydrate the state from the server, and land the
  // user directly on Step 4 (Checkout) with everything intact.
  useEffect(() => {
    const url = new URL(window.location.href);
    const token = url.searchParams.get("pc");
    if (!token) return;
    // Phase 1c — wait until the auth context has resolved before deciding
    // whether to restore or bounce.
    if (authLoading) return;
    if (!collector) {
      nav(`/checkout-auth?pc=${encodeURIComponent(token)}`, { replace: true });
      return;
    }
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/pending-checkouts/${token}`);
        if (!alive) return;
        const pc = r.data;
        setParsed({ items: pc.items || [] });
        setQuote(pc.quote || null);
        if (pc.delivery?.code) setDelivery(pc.delivery.code);
        setCustomer((c) => ({
          ...c,
          name: pc.customer?.name || c.name,
          email: pc.customer?.email || c.email,
          phone: pc.customer?.phone || c.phone,
          instagram_handle: pc.customer?.instagram_handle || c.instagram_handle,
          pinned_instructions: pc.customer?.pinned_instructions || c.pinned_instructions,
        }));
        setPcToken(token);
        setCheckoutMode("account");
        // Re-quote for the delivery method to make sure line prices reflect
        // the current selling price list. Non-fatal if the server call fails.
        try {
          const q = await axios.post(`${API}/orders/quote`, {
            album_slug: pc.album_slug || ALBUM_SLUG,
            variant: pc.variant || "white",
            items: pc.items || [],
            delivery: pc.delivery || { code: "local_pickup" },
            jurisdiction: TAX_JURISDICTION,
          });
          if (alive) setQuote(q.data);
        } catch (_e) { /* keep restored snapshot */ }
        setContactCaptured(true);   // Q1 — restored checkout already has identity
        setStep(5);
      } catch (e) {
        const detail = e?.response?.data?.detail || "Your saved checkout could not be restored.";
        toast.error(detail);
      }
    })();
    return () => { alive = false; };
    // Re-run when auth state resolves or the collector session appears.
  }, [collector, authLoading, nav]);

  // Pre-fill the checkout form if the customer is already signed in.
  useEffect(() => {
    if (collector && !customer.email) {
      setCustomer((c) => ({
        ...c,
        first_name: collector.first_name || c.first_name,
        last_name: collector.last_name || c.last_name,
        name: collector.display_name || collector.full_name || c.name,
        email: collector.email || c.email,
        phone: collector.phone || c.phone,
      }));
    }
  }, [collector, customer.email]);

  // If user is already signed in and reaches the choice step, skip it.
  useEffect(() => {
    if (step === 4 && collector) {
      setCheckoutMode("account");
      setStep(5);
    }
  }, [step, collector]);

  // Scroll to top on step change for cleaner mobile experience.
  useEffect(() => {
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [step]);

  // Cross-Variant Pricing-Integrity · shared fetch wrapper.
  // Every /orders/quote request must go through this so ONE code path
  // owns:
  //   1. Monotonic sequence stamping — late responses whose seq is
  //      behind the latest are discarded before setQuote.
  //   2. AbortController pairing — rapid customer toggles cut the
  //      network connection immediately.
  //   3. Fresh-price merge onto `originalLines` so the visible per-line
  //      unit_price / line_total / display_category always reflects
  //      the tier the backend just returned.
  //
  // Callers pass the item list they want quoted (already filtered by
  // `deselected`). The wrapper returns `{ ok, data, seq, aborted }`.
  const fetchQuoteAndSyncDisplay = useCallback(async (items, deliveryCode, { signal } = {}) => {
    // Cancel any in-flight quote (rapid-toggle race guard).
    try { quoteAbortRef.current?.abort(); } catch (_e) { /* noop */ }
    const controller = new AbortController();
    quoteAbortRef.current = controller;
    // Allow callers (e.g. submit preflight) to compose signals.
    if (signal) {
      if (signal.aborted) controller.abort();
      else signal.addEventListener("abort", () => controller.abort(), { once: true });
    }
    quoteSeqRef.current += 1;
    const mySeq = quoteSeqRef.current;
    try {
      const q = await axios.post(`${API}/orders/quote`, {
        album_slug: effectiveAlbumSlug,
        variant: effectiveVariant,
        collection,
        items,
        delivery: { code: deliveryCode },
        jurisdiction: TAX_JURISDICTION,
      }, { signal: controller.signal });
      // Discard stale responses (a newer toggle already went out).
      if (mySeq !== quoteSeqRef.current) {
        return { ok: false, aborted: true, stale: true, seq: mySeq };
      }
      setQuote(q.data);
      // Build tier-price overrides from the fresh quote so deselected
      // rows also update to the current tier. If the customer re-checks
      // them the visible price is already correct.
      const overrides = {};
      for (const ln of (q.data?.lines || [])) {
        if (ln?.code) overrides[String(ln.code)] = {
          unit_price: Number(ln.unit_price),
          line_total: Number(ln.line_total),
        };
      }
      setOriginalLines((prev) => mergeFreshPrices(prev, q.data?.lines || [], overrides));
      return { ok: true, data: q.data, seq: mySeq };
    } catch (e) {
      if (controller.signal.aborted) {
        return { ok: false, aborted: true, seq: mySeq };
      }
      return { ok: false, error: e, seq: mySeq };
    }
  }, [variant, collection, effectiveAlbumSlug, effectiveVariant]);

  const parseAndQuote = useCallback(async () => {
    if (!raw.trim()) { toast.error("Paste your missing sticker list first."); return; }
    setBusy(true);
    track(EVENT.MISSING_QUOTE_STARTED, {
      source_page: "order-missing",
      user_type:   collector ? "registered" : "guest",
    }, { once: true, key: "quote-session" });
    try {
      const p = await axios.post(`${API}/orders/parse`, {
        album_slug: effectiveAlbumSlug,
        collection,
        raw_text: raw,
      });
      const items = (p.data?.items || []).map((i) => ({ code: i.code, qty: i.qty }));
      const soldOut = ((p.data?.sold_out) || (p.data?.sold_out_codes) || []).length;
      track(EVENT.MISSING_LIST_PARSED, {
        sticker_count:  items.length,
        sold_out_count: soldOut,
        user_type:      collector ? "registered" : "guest",
      });
      // Allow advancing even with 0 items so the customer sees a clear
      // "no valid sticker codes recognised" state on the review page
      // (with the Continue button disabled) — better than a toast.
      const q = await axios.post(`${API}/orders/quote`, {
        album_slug: effectiveAlbumSlug,
        variant: effectiveVariant,
        collection,
        items: items.length ? items : [{ code: "1", qty: 1 }],   // dummy request → we ignore the response lines below
        delivery: { code: delivery },
        jurisdiction: TAX_JURISDICTION,
      });
      // If no items were parsed, blank the quote lines so the review step
      // renders the zero-result UI without misleading price rows.
      if (!items.length) {
        q.data = {
          ...q.data,
          lines: [],
          totals: { ...(q.data.totals || {}), sticker_count: 0, subtotal: 0,
                     tax: null, shipping: null, total: 0 },
          flags: { ...(q.data.flags || {}), no_priceable_items: true },
        };
      }
      setParsed(p.data);
      setQuote(q.data);
      // Phase 4 — capture the customer's ORIGINAL sticker set + reset
      // the selection state. Every sticker starts checked. Also freeze
      // the priced line list so the Review UI can keep rendering every
      // row (with a strike-through when deselected) even after re-quotes
      // filter items out of the response.
      originalItemsRef.current = items;
      setOriginalLines(items.length ? (q.data.lines || []) : []);
      setDeselected(new Set());
      track(EVENT.MISSING_QUOTE_GENERATED, {
        sticker_count:  q.data?.totals?.sticker_count || 0,
        sold_out_count: soldOut,
        order_value:    q.data?.totals?.total || 0,
        currency:       "USD",
        user_type:      collector ? "registered" : "guest",
      }, { once: true, key: q.data?.request_id || `quote-${Date.now()}` });
      // Iter T · Phase 1 — Quote Activity logging. Called ONLY from
      // this primary Review Quote flow. Never awaited so the customer
      // reaches step 2 with zero delay. The Promise reference is held
      // so submit() can wait a short beat for the returned public_id
      // when the customer submits before this call resolves.
      const initialRequestId = q.data?.request_id || null;
      setContactRequestId(initialRequestId);
      quoteLogPromiseRef.current = axios.post(`${API}/orders/quote-activity/log`, {
        album_slug: effectiveAlbumSlug,
        // F3 (owner directive 2026-02-20) — send variant explicitly at
        // the top level so the backend never has to guess. Removes the
        // silent White fallback.
        variant: effectiveVariant,
        collection,
        raw_text: raw,
        parsed_snapshot: p.data || {},
        quote_snapshot: q.data || {},
        session_id: quoteSessionId(),
        request_id: initialRequestId,
        customer: collector ? {
          first_name: collector.first_name || null,
          last_name: collector.last_name || null,
          name: collector.display_name || collector.full_name || null,
          email: collector.email || null,
          phone: collector.phone || null,
          collector_id: collector.public_collector_id || collector.id || null,
        } : null,
      }).then((r) => {
        const id = r?.data?.public_id || null;
        if (id) setSourceQuoteId(id);
        return id;
      }).catch(() => null);
      setStep(2);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  }, [raw, delivery, collector, variant, collection, effectiveAlbumSlug, effectiveVariant]);

  const recomputeQuoteForDelivery = useCallback(async (newDelivery) => {
    if (!parsed) return;
    if (newDelivery === delivery) return; // no-op — spec §2 forbids repeat fires
    const previousShipping = Number(quote?.totals?.shipping?.cost || 0);
    setDelivery(newDelivery);
    setBusy(true);
    try {
      // Phase 4 — respect any customer de-selection when re-quoting for
      // the newly picked delivery method. Route through the shared
      // pricing-integrity wrapper so seq/abort/merge are consistent.
      const items = (parsed.items || [])
        .map((i) => ({ code: i.code, qty: i.qty }))
        .filter((i) => !deselected.has(i.code));
      const r = await fetchQuoteAndSyncDisplay(items, newDelivery);
      if (!r.ok) {
        if (r.stale || r.aborted) return; // superseded — silent
        toast.error(formatApiError(r.error?.response?.data?.detail) || "Could not update the quote.");
        return;
      }
      // Only fire AFTER the backend confirms the new cost — matches the
      // "successful action" rule.
      const newShipping = Number(r.data?.totals?.shipping?.cost || 0);
      track(EVENT.DELIVERY_METHOD_SELECTED, {
        delivery_method: newDelivery,
        added_shipping:  Number((newShipping - previousShipping).toFixed(2)),
        source_page:     "order-missing",
      });
    } finally { setBusy(false); }
  }, [parsed, deselected, delivery, quote, fetchQuoteAndSyncDisplay]);

  // Phase 4 — Live re-quote whenever the customer toggles the selection.
  // Debounced so rapid clicks batch into one server round-trip. The
  // backend is the source of truth for tax + under-35 minimum pricing.
  //
  // Cross-Variant Pricing-Integrity — every response is stamped with a
  // sequence, an AbortController tears down superseded requests, and
  // the resulting quote is merged back into `originalLines` so the
  // visible per-line unit_price / line_total always match the tier
  // the backend just returned. See lib/pricingInvariant.js.
  useEffect(() => {
    if (step !== 3 || !parsed) return;
    if (!originalItemsRef.current.length) return;
    const items = originalItemsRef.current.filter((i) => !deselected.has(i.code));
    let cancelled = false;
    setSelectionBusy(true);
    const t = setTimeout(async () => {
      try {
        if (items.length === 0) {
          // All items removed — reflect a zero total client-side without
          // hitting the backend (which would reject 0 items or dummy).
          setQuote((prev) => prev ? ({
            ...prev,
            totals: {
              ...(prev.totals || {}),
              sticker_count: 0,
              subtotal: 0,
              tax: prev.totals?.tax ? { ...prev.totals.tax, amount: 0 } : null,
              total: 0,
            },
            availability: {
              in_stock_count: 0, out_of_stock_count: 0, not_initialized_count: 0,
              has_out_of_stock: false, has_uninitialized_inventory: false,
            },
          }) : prev);
        } else {
          const r = await fetchQuoteAndSyncDisplay(items, delivery);
          if (!r.ok && !r.stale && !r.aborted && !cancelled) {
            toast.error(formatApiError(r.error?.response?.data?.detail) || "Could not update the quote.");
          }
        }
      } finally {
        if (!cancelled) setSelectionBusy(false);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); setSelectionBusy(false); };
  }, [deselected, step, parsed, delivery, fetchQuoteAndSyncDisplay]);

  const toggleSelection = useCallback((code) => {
    setDeselected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  }, []);

  const restoreAllSelection = useCallback(() => {
    setDeselected(new Set());
  }, []);

  // Phase 5 · Group Actions on Review Quote.
  //   • Check All          → same as Restore All (empty deselected set).
  //   • Uncheck All        → deselect every priced code in the quote.
  //   • Check Out of Stock → restore only the previously-removed OOS items.
  //   • Uncheck OOS        → deselect only items currently marked out-of-stock.
  //   • Restore Category   → per-category header action; restores every
  //     deselected item that belongs to a given `display_category`.
  const uncheckAllSelection = useCallback(() => {
    const codes = (originalLines || []).map((l) => l.code);
    if (codes.length === 0) return;
    setDeselected(new Set(codes));
  }, [originalLines]);

  const _outOfStockCodes = useCallback(() => {
    return (originalLines || [])
      .filter((l) => l?.availability?.availability_status === "out_of_stock")
      .map((l) => l.code);
  }, [originalLines]);

  const checkOutOfStock = useCallback(() => {
    const oos = new Set(_outOfStockCodes());
    if (oos.size === 0) return;
    setDeselected((prev) => {
      const next = new Set(prev);
      for (const c of oos) next.delete(c);
      return next;
    });
  }, [_outOfStockCodes]);

  const uncheckOutOfStock = useCallback(() => {
    const oos = _outOfStockCodes();
    if (oos.length === 0) return;
    setDeselected((prev) => {
      const next = new Set(prev);
      for (const c of oos) next.add(c);
      return next;
    });
  }, [_outOfStockCodes]);

  const restoreCategory = useCallback((categoryKey) => {
    const codesInCat = (originalLines || [])
      .filter((l) => (l.display_category || "other") === categoryKey)
      .map((l) => l.code);
    if (codesInCat.length === 0) return;
    setDeselected((prev) => {
      const next = new Set(prev);
      for (const c of codesInCat) next.delete(c);
      return next;
    });
  }, [originalLines]);

  // Missing-Sticker Ordering — Select-All / Unselect-All controls per
  // quote group. These operate ONLY on the codes belonging to
  // `categoryKey`, leaving every other group untouched.
  const selectCategory = useCallback((categoryKey) => {
    const codesInCat = (originalLines || [])
      .filter((l) => (l.display_category || "other") === categoryKey)
      .map((l) => l.code);
    if (codesInCat.length === 0) return;
    setDeselected((prev) => {
      const next = new Set(prev);
      for (const c of codesInCat) next.delete(c);
      return next;
    });
  }, [originalLines]);

  const unselectCategory = useCallback((categoryKey) => {
    const codesInCat = (originalLines || [])
      .filter((l) => (l.display_category || "other") === categoryKey)
      .map((l) => l.code);
    if (codesInCat.length === 0) return;
    setDeselected((prev) => {
      const next = new Set(prev);
      for (const c of codesInCat) next.add(c);
      return next;
    });
  }, [originalLines]);

  const submitContactCapture = useCallback(async () => {
    // Phase Q1 — Lead Capture. Gates the detailed quote reveal. All
    // four identity fields are mandatory (first_name, last_name, email,
    // phone). Newsletter opt-in is optional and never blocks the quote.
    const fn = (customer.first_name || "").trim();
    const ln = (customer.last_name || "").trim();
    const em = (customer.email || "").trim();
    const ph = (customer.phone || "").trim();
    if (!fn) { toast.error("Please enter your first name."); return; }
    if (!ln) { toast.error("Please enter your last name."); return; }
    if (!em) { toast.error("Please enter your email address."); return; }
    // Cheap client-side email shape check — the backend still validates
    // authoritatively via EmailStr.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (!ph) { toast.error("Please enter your phone number."); return; }

    setBusy(true);
    try {
      // Phase 4 · lightweight email typo suggestion + domain check.
      // We ONLY block on obvious typos when the customer has not yet
      // confirmed the original. `emailTypoConfirmed` tracks their
      // last decision so re-hitting Continue doesn't loop the prompt.
      if (!emailTypoConfirmed) {
        try {
          const v = await axios.post(`${API}/orders/validate-email`, { email: em });
          const vd = v?.data || {};
          if (vd.suggestion && vd.suggestion !== em) {
            setEmailValidation(vd);
            setEmailSuggestion(vd.suggestion);
            setShowEmailSuggestion(true);
            setBusy(false);
            return;      // wait for user's Use / Keep decision
          }
          if (vd.validation_status === "invalid_domain") {
            toast.error(
              "We couldn't find a working email domain for this address. Please check your email and try again."
            );
            setBusy(false);
            return;
          }
          // Silent when valid or dns_unavailable — proceed.
        } catch (_e) {
          // Never block a customer's quote on a validation blip.
        }
      }
      // Persist the customer identity onto the same quote-activity
      // record (idempotent upsert on request_id + session_id). The
      // detailed quote reveal MUST not depend on this call succeeding,
      // but we surface an error so the user knows if their lead didn't
      // save. The backend is fire-and-forget-safe.
      const payload = {
        album_slug: ALBUM_SLUG,
        // F3 · explicit variant at the top level.
        variant,
        raw_text: raw,
        parsed_snapshot: parsed || {},
        quote_snapshot: quote || {},
        session_id: quoteSessionId(),
        request_id: contactRequestId || quote?.request_id || null,
        customer: {
          first_name: fn,
          last_name: ln,
          name: `${fn} ${ln}`.trim(),
          email: em,
          phone: ph,
          collector_id: collector?.public_collector_id || collector?.id || null,
        },
        newsletter_opt_in: !!newsletterOptIn,
        // Phase 4 · persist the client-side view of the validation
        // outcome so admin analytics can distinguish overridden-typo
        // vs corrected-typo vs valid. Server re-derives if absent.
        email_validation_status: emailTypoConfirmed
          ? (emailSuggestion ? "typo_overridden" : (emailValidation?.validation_status || "valid"))
          : (emailValidation?.validation_status || "valid"),
      };
      const r = await axios.post(`${API}/orders/quote-activity/log`, payload);
      const id = r?.data?.public_id || null;
      if (id) setSourceQuoteId(id);
      // Also keep the canonical display name in sync so downstream
      // checkout doesn't have to recompose it.
      setCustomer((c) => ({
        ...c,
        first_name: fn,
        last_name: ln,
        name: `${fn} ${ln}`.trim(),
        email: em,
        phone: ph,
      }));
      setContactCaptured(true);
      setStep(3);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not save your details. Please try again.");
    } finally {
      setBusy(false);
    }
  }, [customer, raw, parsed, quote, contactRequestId, collector, newsletterOptIn,
       emailTypoConfirmed, emailValidation, emailSuggestion]);

  const submit = useCallback(async (accountPayload) => {
    // Missing-Sticker Ordering · Customer Identity Gate — the backend
    // enforces first_name + last_name separately. Mirror that on the
    // client so we surface a friendly toast before the network round
    // trip. Account-mode submissions inherit identity from the
    // authenticated collector below.
    const isAccount = Boolean(accountPayload) || Boolean(collector);
    if (!isAccount) {
      if (!(customer.first_name || "").trim())
        return toast.error("Please enter your first name.");
      if (!(customer.last_name || "").trim())
        return toast.error("Please enter your last name.");
    }
    if (!customer.email.trim()) return toast.error("Please enter your email.");
    if (!customer.phone.trim()) return toast.error("Phone number is required.");
    // Phase 3A.1 — Enforce shipping address on non-pickup orders.
    const needsShipping = delivery !== "local_pickup";
    const a = customer.shipping_address || {};
    if (needsShipping) {
      const missing = ["line1", "city", "state", "postal_code"].filter((k) => !(a[k] || "").trim());
      if (missing.length) {
        return toast.error(`Please complete your shipping address (${missing.join(", ")}).`);
      }
    }
    const items = (parsed?.items || [])
      .map((i) => ({ code: i.code, qty: i.qty }))
      .filter((i) => !deselected.has(i.code));
    if (items.length === 0) {
      return toast.error("You've removed every sticker from your quote. Please restore at least one to continue.");
    }
    setBusy(true);
    try {
      // Cross-Variant Pricing-Integrity · Pre-submit invariant.
      // Refetch the quote for the CURRENT selection one last time so
      // the visible line prices and the totals are guaranteed to come
      // from the SAME fresh snapshot. Then run validatePricingInvariant
      // and refuse to submit on any mismatch. See Msg 240.
      setPricingIntegrityBlock(null);
      const preflight = await fetchQuoteAndSyncDisplay(items, delivery);
      if (!preflight.ok && !preflight.stale && !preflight.aborted) {
        toast.error(formatApiError(preflight.error?.response?.data?.detail) || "Could not refresh your quote.");
        setBusy(false);
        return;
      }
      const freshQuote = preflight.data || quote;
      const linesForCheck = mergeFreshPrices(originalLines, freshQuote?.lines || []);
      const check = validatePricingInvariant({
        lines: linesForCheck,
        isSelected: (code) => !deselected.has(code),
        totals: freshQuote?.totals || {},
      });
      if (!check.ok) {
        setPricingIntegrityBlock(check);
        toast.error("Your prices have just changed. We refreshed your quote — please review before submitting.");
        setBusy(false);
        return;
      }

      // If the customer chose the account path AND is not already signed in,
      // create the account first — this signs them in via cookies and links
      // any prior guest orders with the same email.
      if (accountPayload?.password && !collector) {
        await axios.post(`${API}/collector-auth/checkout-register`, accountPayload, {
          withCredentials: true,
        });
        await refreshAuth();
      }

      // Only forward the shipping address when needed. This preserves the
      // customer's locally captured address across pickup toggles without
      // ever persisting an empty block on the order document.
      const customerPayload = { ...customer };
      // Missing-Sticker Ordering · Customer Identity Gate — for the
      // signed-in path we synthesize first/last from the authenticated
      // collector so the identity travels with the order even when the
      // Contact card was pre-hidden.
      if (isAccount && collector) {
        const dn = (collector.display_name || collector.full_name || "").trim();
        const [af, ...ar] = dn.split(/\s+/);
        if (!customerPayload.first_name)
          customerPayload.first_name = collector.first_name || af || "Collector";
        if (!customerPayload.last_name)
          customerPayload.last_name = collector.last_name || ar.join(" ") || "Account";
        if (!customerPayload.email) customerPayload.email = collector.email || "";
        if (!customerPayload.phone) customerPayload.phone = collector.phone || "";
      }
      // Always send a composed `name` so legacy readers still work.
      if (!customerPayload.name) {
        customerPayload.name = [customerPayload.first_name, customerPayload.last_name]
          .filter(Boolean).join(" ").trim();
      }
      if (!needsShipping) delete customerPayload.shipping_address;

      // Iter T · Phase 1 — resolve the source_quote_public_id if the
      // logging call is still in flight. Cap the wait so a slow logger
      // never delays checkout more than 1.5s. If it times out we submit
      // without the link — customer never sees a failure and the order
      // is created normally.
      let source_quote_public_id = sourceQuoteId;
      if (!source_quote_public_id && quoteLogPromiseRef.current) {
        try {
          source_quote_public_id = await Promise.race([
            quoteLogPromiseRef.current,
            new Promise((resolve) => setTimeout(() => resolve(null), 1500)),
          ]);
        } catch (_e) { source_quote_public_id = null; }
      }

      const r = await axios.post(`${API}/orders/submit`, {
        album_slug: effectiveAlbumSlug,
        variant: effectiveVariant,
        collection,
        items,
        delivery: { code: delivery },
        jurisdiction: TAX_JURISDICTION,
        customer: customerPayload,
        pending_checkout_token: pcToken || null,
        source_quote_public_id: source_quote_public_id || null,
        usps_acknowledgment: !!(accountPayload?.usps_acknowledgment),
      }, { withCredentials: true });
      const { public_id, magic_token } = r.data;
      // Anonymous conversion event — never sends the order id or
      // customer identity. Idempotent per new order to survive React
      // StrictMode double-effects and retry double-clicks.
      track(EVENT.MISSING_ORDER_SUBMITTED, {
        sticker_count:   items.length,
        order_value:     Number(r.data?.pricing_snapshot?.totals?.total || quote?.totals?.total || 0),
        currency:        "USD",
        delivery_method: delivery,
        user_type:       collector ? "registered" : "guest",
      }, { once: true, key: public_id });
      // Quote-to-order conversion — fires ONLY when a quote was resumed
      // into this checkout. Idempotent per resulting order so a retry
      // never double-counts. Quote id + order id stay local.
      if (source_quote_public_id) {
        track(EVENT.QUOTE_CONVERTED_TO_ORDER, {
          conversion_actor: "customer",
          delivery_method:  delivery,
          order_value:      Number(r.data?.pricing_snapshot?.totals?.total || quote?.totals?.total || 0),
          currency:         "USD",
        }, { once: true, key: `q2o-${public_id}` });
      }
      // Meta Pixel — Lead fires ONLY after the backend confirmed the
      // submission (`public_id` returned). Uses a stable event_id so
      // any server-side CAPI Lead the backend later dispatches
      // dedupes cleanly against this browser event.
      trackPixelEvent("Lead", {
        value:            Number(r.data?.pricing_snapshot?.totals?.total || quote?.totals?.total || 0),
        currency:         "USD",
        content_name:     "Missing Stickers Order Submitted",
        content_category: "Panini World Cup 2026 Missing Stickers",
      }, { once: true, eventId: `lead_${public_id}` });
      toast.success("Order submitted — bookmark this page.");
      nav(`/orders/${public_id}?t=${magic_token}`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Submit failed.");
    } finally { setBusy(false); }
  }, [customer, parsed, delivery, nav, collector, refreshAuth, pcToken, sourceQuoteId, deselected, fetchQuoteAndSyncDisplay, quote, originalLines]);

  return (
    <div data-testid="missing-page" ref={topRef}>
      <SEO
        title="Instant Missing Stickers Quote — Panini FIFA World Cup 2026"
        description="Paste your missing sticker list. See a transparent quote instantly. Pay by Zelle, ship or pick up in Miami."
        // Pin the approved global social preview explicitly so this
        // route never falls back to `settings.og_image_url` (which was
        // previously set to a personal Panini sticker asset and is
        // still surfacing on Facebook/WhatsApp caches for this page).
        // Every other public route resolves the same URL via SEO's
        // internal fallback chain; setting it explicitly here guarantees
        // parity even if a stale settings row / SSR cache path serves
        // this URL to crawlers.
        image="/og-collectorup.jpg"
      />

      {/* Hero */}
      <section className="hero-black">
        <div className="container-gm py-5 md:py-7 lg:py-8">
          <div className="max-w-3xl">
            <span className="sticker sticker-red">MISSING STICKERS · INSTANT QUOTE</span>
            <h1 className="h-display text-white text-3xl sm:text-4xl mt-3">
              Paste your list.{" "}
              <span className="text-[var(--panini-yellow)]">See your price. Instantly.</span>
            </h1>
            <p className="text-white/85 mt-3 text-sm leading-relaxed max-w-2xl">
              The exact same quote Gil would send you on Instagram — now on the website.
              We&apos;ll break it down sticker-by-sticker and take you straight to checkout.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="album-collection-selector-hero" style={{display:"none"}}>
              {/* Phase 4 · The collection selector lives inside Step 1
                  now (see StepPaste). This hero strip is intentionally
                  hidden so the collection choice is visually grouped
                  with the sticker input controls. */}
            </div>
          </div>
        </div>
      </section>

      <div className="container-gm py-4 md:py-6 max-w-5xl pb-24 lg:pb-6">
        <Stepper current={step} />

        {step === 1 && (
          <StepPaste
            raw={raw}
            setRaw={setRaw}
            onNext={parseAndQuote}
            busy={busy}
            variant={variant}
            setVariant={setVariantAndReset}
            blueReady={blueReady}
            goldReady={goldReady}
            collection={collection}
            setCollection={setCollectionAndReset}
          />
        )}

        {pricingIntegrityBlock && step >= 3 && (
          <div
            data-testid="pricing-integrity-block"
            role="alert"
            className="mb-4 rounded-xl border-2 border-amber-400 bg-amber-50 p-4 text-sm text-amber-900"
          >
            <div className="font-semibold text-amber-900 mb-1">
              Prices refreshed — please review before submitting
            </div>
            <p className="leading-relaxed">
              We noticed the item prices you saw and the order total no longer matched
              (your selection just crossed a pricing threshold). We&apos;ve pulled the
              latest prices from the price list and updated your quote below. Please
              confirm the new total, then continue.
            </p>
            {typeof pricingIntegrityBlock.delta === "number" && (
              <p className="mt-1 text-xs text-amber-800/80">
                Adjustment: <strong data-testid="integrity-delta">${Math.abs(pricingIntegrityBlock.delta).toFixed(2)}</strong>
                {" "}({pricingIntegrityBlock.reason.replace(/_/g, " ")})
              </p>
            )}
            <button
              type="button"
              data-testid="pricing-integrity-dismiss"
              onClick={() => setPricingIntegrityBlock(null)}
              className="mt-2 inline-flex items-center gap-1 rounded-md border border-amber-400 bg-white px-3 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100"
            >
              Got it, I&apos;ve reviewed the new prices
            </button>
          </div>
        )}

        {step === 2 && quote && parsed && (
          <StepContactCapture
            customer={customer}
            setCustomer={setCustomer}
            newsletterOptIn={newsletterOptIn}
            setNewsletterOptIn={setNewsletterOptIn}
            collector={collector}
            busy={busy}
            onBack={() => setStep(1)}
            onSubmit={submitContactCapture}
          />
        )}

        {step === 3 && quote && parsed && (
          <StepReview
            quote={quote}
            parsed={parsed}
            variant={variant}
            onBack={() => setStep(1)}
            onNext={() => {
              track(EVENT.MISSING_CHECKOUT_STARTED, {
                sticker_count:   quote?.totals?.sticker_count || 0,
                order_value:     Number(quote?.totals?.total || 0),
                currency:        "USD",
                user_type:       collector ? "registered" : "guest",
              }, { once: true, key: `checkout-${sourceQuoteId || "session"}` });
              setStep(4);
            }}
            deselected={deselected}
            onToggleSelection={toggleSelection}
            onRestoreAll={restoreAllSelection}
            onUncheckAll={uncheckAllSelection}
            onCheckOutOfStock={checkOutOfStock}
            onUncheckOutOfStock={uncheckOutOfStock}
            onRestoreCategory={restoreCategory}
            onSelectCategory={selectCategory}
            onUnselectCategory={unselectCategory}
            sourceQuoteId={sourceQuoteId}
            selectionBusy={selectionBusy}
            originalLines={originalLines}
            delivery={delivery}
            adminAdjustments={adminFinalized ? adminAdjustments : null}
            feedbackAuth={{
              publicId:         sourceQuoteId,
              resumeToken:      resumeToken,
              requestId:        resumeToken ? null : contactRequestId,
              sessionId:        resumeToken ? null : quoteSessionId(),
              source:           feedbackSource,
              existingFeedback: existingFeedback,
            }}
          />
        )}

        {step === 4 && quote && (
          <StepChoice
            onGuest={() => { setCheckoutMode("guest"); setStep(5); }}
            onCreateAccount={async () => {
              // Phase 1c — persist a server-side pending checkout draft
              // BEFORE navigating to the auth screen so the order survives
              // Google OAuth / email register / sign-in / browser refresh.
              if (collector) {
                // Already signed in — skip auth screen entirely.
                setCheckoutMode("account");
                setStep(5);
                return;
              }
              try {
                const r = await axios.post(`${API}/pending-checkouts`, {
                  album_slug: ALBUM_SLUG,
                  variant,
                  items: (parsed?.items || [])
                    .map((i) => ({ code: i.code, qty: i.qty }))
                    .filter((i) => !deselected.has(i.code)),
                  delivery: { code: delivery },
                  customer: {
                    name: customer.name || null,
                    email: customer.email || null,
                    phone: customer.phone || null,
                    instagram_handle: customer.instagram_handle || null,
                    pinned_instructions: customer.pinned_instructions || null,
                  },
                  quote,
                });
                nav(`/checkout-auth?pc=${encodeURIComponent(r.data.token)}`);
              } catch (e) {
                toast.error(formatApiError(e?.response?.data?.detail) || "Could not save your order.");
              }
            }}
            onBack={() => setStep(3)}
          />
        )}

        {step === 5 && quote && (
          <StepCheckout
            quote={quote}
            customer={customer}
            setCustomer={setCustomer}
            delivery={delivery}
            onChangeDelivery={recomputeQuoteForDelivery}
            onBack={() => setStep(4)}
            onSubmit={submit}
            busy={busy}
            // If the user is already signed in (either directly or through
            // the pending-checkout restore) treat this as a guest-style
            // checkout — the account already exists; don't re-ask for a
            // password.
            mode={collector ? "guest" : (checkoutMode || "guest")}
          />
        )}

        <ContactFooter />
      </div>

      {/* Phase 4 · Email typo confirm-first prompt.
          Hoisted here (from StepReview) because every referenced piece
          of state — showEmailSuggestion, emailSuggestion,
          customer, submitContactCapture, and the setters — lives on
          the parent MissingStickers scope. Rendering it inside
          StepReview caused ReferenceError → blank Step 3 page. */}
      <EmailTypoSuggestionDialog
        open={showEmailSuggestion}
        originalEmail={customer.email || ""}
        suggestedEmail={emailSuggestion}
        onKeep={() => {
          setShowEmailSuggestion(false);
          setEmailTypoConfirmed(true);
          // Re-fire the continue action now that the user has decided.
          setTimeout(() => submitContactCapture(), 0);
        }}
        onUse={() => {
          const s = emailSuggestion;
          setCustomer((c) => ({ ...c, email: s }));
          setShowEmailSuggestion(false);
          setEmailTypoConfirmed(true);
          setEmailValidation((v) => v
            ? { ...v, validation_status: "typo_corrected", normalized_email: s }
            : { validation_status: "typo_corrected", normalized_email: s });
          setTimeout(() => submitContactCapture(), 0);
        }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  Stepper (Paste → Review → Customer Details)
// ─────────────────────────────────────────────────────────────
function Stepper({ current }) {
  const steps = ["Paste", "Contact", "Review", "Continue", "Checkout"];
  return (
    <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="missing-stepper">
      {steps.map((s, i) => {
        const n = i + 1;
        const active = n === current;
        const done = n < current;
        return (
          <React.Fragment key={s}>
            <div
              className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border-2 text-xs uppercase tracking-widest font-bold transition
                ${active ? "bg-[var(--panini-yellow)] border-[var(--ink)] text-[var(--ink)]"
                  : done ? "bg-emerald-100 border-emerald-900 text-emerald-900"
                  : "bg-white border-[var(--stone)] text-[var(--smoke)]"}`}
              data-testid={`step-${n}-${active ? "active" : done ? "done" : "todo"}`}
            >
              {done && <CheckCircle2 size={12} />}
              <span>{n}. {s}</span>
            </div>
            {i < steps.length - 1 && <div className="w-4 h-[2px] bg-[var(--stone)]" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  Step 1 — Paste
// ─────────────────────────────────────────────────────────────
function StepPaste({ raw, setRaw, onNext, busy, variant, setVariant, blueReady, goldReady, collection, setCollection }) {
  const isRussia = collection === "russia_2018";
  return (
    <div className="space-y-5" data-testid="paste-step">
      <div className="rounded-2xl border-4 border-[var(--ink)] bg-white p-4 md:p-5 shadow-[6px_6px_0_var(--panini-yellow)]">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="kicker kicker-red">Step 1</div>
            <h2 className="h-display text-xl md:text-2xl mt-1">Paste your missing list.</h2>
            <p className="text-[var(--smoke)] text-xs md:text-sm mt-1.5 max-w-xl leading-relaxed">
              Send it exactly the way you&apos;d send it to Gil on Instagram — countries,
              numbers, ranges. We&apos;ll parse it, price it, give you a transparent quote.
            </p>
          </div>
          <div className="rounded-full border-2 border-[var(--panini-yellow)] bg-[var(--panini-yellow)]/20 px-3 py-1 text-[10px] md:text-xs font-bold uppercase tracking-widest text-[var(--ink)] flex items-center gap-1.5">
            <Zap size={12} /> No signup. No credit card.
          </div>
        </div>

        {/* Phase 4 · Collection selector — grouped with the sticker
            input controls, placed directly above the variant row. */}
        <div
          className="mt-4 rounded-xl border-2 border-[var(--ink)] bg-white p-3 flex items-center justify-between gap-3 flex-wrap"
          data-testid="collection-selector"
        >
          <div>
            <div className="text-[11px] uppercase tracking-widest font-bold text-[var(--ink)]">
              Collection
            </div>
            <div className="text-[11px] text-[var(--smoke)] mt-0.5">
              {isRussia
                ? "Panini Russia 2018 Black Back · numeric codes only."
                : "Panini FIFA World Cup 2026 · country + number codes."}
            </div>
          </div>
          <div className="inline-flex rounded-full border-2 border-[var(--ink)] overflow-hidden flex-wrap">
            <button
              type="button"
              onClick={() => setCollection("wc2026")}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest ${
                !isRussia
                  ? "bg-[var(--panini-yellow)] text-[var(--ink)]"
                  : "bg-white text-[var(--ink)] hover:bg-neutral-50"
              }`}
              data-testid="collection-toggle-wc2026"
              aria-pressed={!isRussia}
            >
              World Cup 2026
            </button>
            <button
              type="button"
              onClick={() => setCollection("russia_2018")}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest border-l-2 border-[var(--ink)] ${
                isRussia
                  ? "bg-[var(--panini-red)] text-white"
                  : "bg-white text-[var(--ink)] hover:bg-neutral-50"
              }`}
              data-testid="collection-toggle-russia-2018"
              aria-pressed={isRussia}
            >
              Russia 2018
            </button>
          </div>
        </div>

        {/* Blue Parallel · sticker version selector. Defaults to White
            so the existing customer flow is unchanged.
            Phase 4 · Hidden entirely when Russia 2018 is active —
            Russia uses its single `black_back` variant internally. */}
        {!isRussia && (
        <div
          className="mt-4 rounded-xl border-2 border-[var(--ink)] bg-white p-3 flex items-center justify-between gap-3 flex-wrap"
          data-testid="variant-selector"
        >
          <div>
            <div className="text-[11px] uppercase tracking-widest font-bold text-[var(--ink)]">
              Which version do you want quoted?
            </div>
            <div className="text-[11px] text-[var(--smoke)] mt-0.5">
              {variant === "gold_crumple"
                ? "Gold Crumple prices apply (single price per sticker)."
                : variant === "blue"
                  ? "Blue Parallel prices apply."
                  : "Standard White / Regular prices apply."}
            </div>
          </div>
          <div className="inline-flex rounded-full border-2 border-[var(--ink)] overflow-hidden flex-wrap">
            <button
              type="button"
              onClick={() => setVariant("white")}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest ${
                variant === "white"
                  ? "bg-[var(--panini-yellow)] text-[var(--ink)]"
                  : "bg-white text-[var(--ink)] hover:bg-neutral-50"
              }`}
              data-testid="variant-toggle-white"
              aria-pressed={variant === "white"}
            >
              White / Regular
            </button>
            <button
              type="button"
              onClick={() => blueReady && setVariant("blue")}
              disabled={!blueReady}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest border-l-2 border-[var(--ink)] ${
                variant === "blue"
                  ? "bg-[#2C6EF2] text-white"
                  : blueReady
                    ? "bg-white text-[var(--ink)] hover:bg-neutral-50"
                    : "bg-neutral-100 text-[var(--smoke)]/60 cursor-not-allowed"
              }`}
              data-testid="variant-toggle-blue"
              aria-pressed={variant === "blue"}
              aria-disabled={!blueReady}
              title={blueReady ? "Quote using Blue Parallel prices"
                                 : "Blue Parallel is not yet available."}
            >
              Blue Parallel{blueReady ? "" : " · Coming soon"}
            </button>
            <button
              type="button"
              onClick={() => goldReady && setVariant("gold_crumple")}
              disabled={!goldReady}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest border-l-2 border-[var(--ink)] ${
                variant === "gold_crumple"
                  ? "bg-amber-500 text-black"
                  : goldReady
                    ? "bg-white text-[var(--ink)] hover:bg-amber-50"
                    : "bg-neutral-100 text-[var(--smoke)]/60 cursor-not-allowed"
              }`}
              data-testid="variant-toggle-gold-crumple"
              aria-pressed={variant === "gold_crumple"}
              aria-disabled={!goldReady}
              title={goldReady ? "Quote using Gold Crumple prices"
                                 : "Gold Crumple is not yet available."}
            >
              Gold Crumple{goldReady ? "" : " · Coming soon"}
            </button>
          </div>
        </div>
        )}

        <textarea
          rows={8}
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          style={{ minHeight: "200px", maxHeight: "480px" }}
          className="mt-3 w-full border-2 border-[var(--ink)] rounded-xl p-3 font-mono text-sm leading-6 focus:outline-none focus:ring-4 focus:ring-[var(--panini-yellow)]"
          placeholder={isRussia ? "Numbers only. Examples: 00, 1, 117, 681." : PLACEHOLDER}
          maxLength={20000}
          data-testid="paste-textarea"
        />

        <div className="mt-3 flex items-center justify-between gap-4 flex-wrap">
          <p className="text-xs text-[var(--smoke)] max-w-lg leading-relaxed">
            <strong className="text-[var(--ink)]">Accepted formats:</strong> country codes with colons
            (<code className="font-mono">MEX: 4, 6, 10</code>), single tokens (<code className="font-mono">FWC-3</code>,
            <code className="font-mono"> ARG-15</code>), plain numbers, ranges (<code className="font-mono">5-9</code>),
            and duplicates. Flag emojis, app headings, and download URLs are ignored automatically.
          </p>
          <button
            onClick={onNext}
            disabled={busy || !raw.trim()}
            className="btn-red text-sm md:text-base"
            data-testid="paste-next"
          >
            {busy ? <><Loader2 size={16} className="animate-spin" /> Preparing quote…</> :
                    <><Wand2 size={16} /> Review Quote <ArrowRight size={16} /></>}
          </button>
        </div>
        <div className="mt-3 text-xs text-[var(--smoke)] text-right">
          Already have a Quote ID?{" "}
          <a href="/order-missing/resume" className="underline text-[var(--panini-red)] font-bold" data-testid="resume-quote-link">
            Resume a Quote
          </a>
        </div>
      </div>

      {/* Example cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <ExampleCard
          title="Example — country + numbers"
          sample={`MEX: 13\nRSA: 4\nKOR: 15\nNED: 1, 10, 18\nSWE: 4, 5`}
        />
        <ExampleCard
          title="Example — mixed with ranges"
          sample={`ARG: 5-9\nBRA: 12\nFWC: 3, 7\nCC: 1, 4, 6`}
        />
      </div>

      {/* Phase A · Live Sold-Out Stickers.
          Compact, collapsible customer-facing section fed by the
          `/api/public/inventory/sold-out` endpoint. Never blocks the
          input step — endpoint failures render a neutral notice and
          the page continues to work. */}
      <LiveSoldOutSection />
    </div>
  );
}

/**
 * Live sold-out list rendered directly below the input examples on the
 * /order-missing input step. Uses the shared `soldOutList` formatter so
 * the grouping / dedupe / album-order / FWC00 rules match the admin
 * Copy Sold-Out button exactly.
 *
 * Refresh strategy — pulls the public endpoint every 30 seconds while
 * the tab is visible so admin sold-out / back-in-stock changes surface
 * without a hard reload. The relative-time label ticks every 30
 * seconds independently so "Updated 2 minutes ago" stays fresh even
 * between poll cycles. Neither the fetch nor the tick ever mutates
 * the stored canonical timestamp.
 */
const LIVE_SOLD_OUT_POLL_MS = 30_000;
const LIVE_SOLD_OUT_TICK_MS = 30_000;

function LiveSoldOutSection() {
  const [state, setState] = React.useState({
    loading: true, data: null, error: "",
  });
  const [expanded, setExpanded] = React.useState(false);
  // Force `formatRelative` to recompute every LIVE_SOLD_OUT_TICK_MS
  // without touching the canonical timestamp itself.
  const [, setTick] = React.useState(0);

  const load = React.useCallback(async () => {
    try {
      const r = await api.get("/public/inventory/sold-out");
      setState({ loading: false, data: r.data, error: "" });
    } catch (e) {
      setState({ loading: false, data: null, error: "unavailable" });
    }
  }, []);

  React.useEffect(() => {
    load();
    let pollId = null;
    const startPolling = () => {
      if (pollId != null) return;
      pollId = setInterval(load, LIVE_SOLD_OUT_POLL_MS);
    };
    const stopPolling = () => {
      if (pollId == null) return;
      clearInterval(pollId);
      pollId = null;
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        load();
        startPolling();
      } else {
        stopPolling();
      }
    };
    if (document.visibilityState === "visible") startPolling();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [load]);

  // Relative-time ticker — pure clock advancement, no fetches, no
  // timestamp writes.
  React.useEffect(() => {
    const id = setInterval(() => setTick((n) => (n + 1) % 1_000_000),
      LIVE_SOLD_OUT_TICK_MS);
    return () => clearInterval(id);
  }, []);

  // Merge codes from the two customer-facing categories. Uninitialized
  // categories are ignored per the Phase A brief — an uninitialized
  // category is NOT the same as sold-out.
  const merged = React.useMemo(() => {
    const cats = state.data?.categories || {};
    const out = [];
    for (const key of ["white_regular", "coca_cola"]) {
      const c = cats[key];
      if (c?.initialized) out.push(...(c.codes || []));
    }
    return out;
  }, [state.data]);
  const payload = React.useMemo(() => buildSoldOutPayload(merged), [merged]);

  const updatedAtISO = state.data?.updated_at
    || state.data?.inventory_updated_at   // legacy field name compat
    || null;
  const relativeLabel = updatedAtISO ? formatRelative(updatedAtISO) : "";
  const absoluteLabel = updatedAtISO ? formatAbsoluteLocal(updatedAtISO) : "";

  if (state.error === "unavailable") {
    return (
      <div className="rounded-2xl border-2 border-[var(--stone)] bg-white p-4 text-sm text-[var(--smoke)]"
           data-testid="live-sold-out-error">
        Live inventory is temporarily unavailable. You may still request a quote.
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl border-2 border-[var(--ink)] bg-white p-4 md:p-5"
      data-testid="live-sold-out-section"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[var(--panini-red)]">
            Live Inventory
          </div>
          <h2 className="font-display text-lg md:text-xl mt-0.5"
              data-testid="live-sold-out-heading">
            Live Sold-Out Stickers
          </h2>
        </div>
        {updatedAtISO ? (
          // Wrapper carries the tooltip (desktop hover) AND the
          // absolute-time <time> element (screen readers + mobile).
          <div
            className="text-[10px] uppercase tracking-widest text-[var(--smoke)] text-right"
            data-testid="live-sold-out-updated-at"
            data-updated-at-iso={updatedAtISO}
          >
            <span
              className="cursor-help"
              title={absoluteLabel}
              data-testid="live-sold-out-updated-relative"
            >
              {relativeLabel}
            </span>
            <time
              dateTime={updatedAtISO}
              className="block normal-case tracking-normal text-[10px] text-[var(--smoke)] opacity-80"
              data-testid="live-sold-out-updated-absolute"
            >
              {absoluteLabel}
            </time>
          </div>
        ) : (
          // Missing/invalid canonical timestamp AND no legacy fallback
          // was resolvable. Never render nothing — surface the state
          // explicitly so the operator knows the tracking metadata
          // needs attention.
          <div
            className="text-[10px] uppercase tracking-widest text-[var(--smoke)] text-right italic"
            data-testid="live-sold-out-updated-unavailable"
          >
            Inventory update time unavailable
          </div>
        )}
      </div>

      <p className="mt-2 text-xs md:text-sm text-[var(--smoke)] leading-relaxed"
         data-testid="live-sold-out-help">
        These stickers are currently sold out based on our live inventory. You
        may still include sold-out stickers in your request. We&apos;ll explain
        the available fulfillment options when your quote is generated.
      </p>

      {state.loading ? (
        <div className="mt-3 text-xs text-[var(--smoke)]"
             data-testid="live-sold-out-loading">
          Loading live inventory…
        </div>
      ) : payload.isEmpty ? (
        <div className="mt-3 text-sm font-mono text-emerald-800"
             data-testid="live-sold-out-empty">
          {payload.text}
        </div>
      ) : (
        <div className="mt-3" data-testid="live-sold-out-list-wrap">
          <pre
            className={
              "font-mono text-sm leading-6 whitespace-pre-wrap break-words " +
              (expanded ? "" : "max-h-[8rem] overflow-hidden")
            }
            data-testid="live-sold-out-list"
            data-expanded={expanded}
          >{payload.text}</pre>
          {payload.groups.length > 3 && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-2 text-xs uppercase tracking-widest font-bold text-[var(--panini-red)] hover:underline"
              data-testid="live-sold-out-toggle"
            >
              {expanded ? "Show less" : `Show all ${payload.totalCodes} codes`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function ExampleCard({ title, sample }) {
  return (
    <div className="rounded-2xl border-4 border-[var(--ink)] bg-[var(--ink)] text-white p-5 md:p-6 shadow-[6px_6px_0_var(--panini-red)]">
      <div className="text-[10px] uppercase tracking-widest text-[var(--panini-yellow)] font-mono">{title}</div>
      <pre className="mt-3 font-mono text-sm leading-7 whitespace-pre-wrap">{sample}</pre>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  Step 2 — Contact Capture (Phase Q1 lead gate)
//  Blocks the detailed quote until First name / Last name / Email /
//  Phone are captured. Newsletter opt-in is optional and never blocks.
// ─────────────────────────────────────────────────────────────
function StepContactCapture({
  customer,
  setCustomer,
  newsletterOptIn,
  setNewsletterOptIn,
  collector,
  busy,
  onBack,
  onSubmit,
}) {
  const setField = (k, v) => setCustomer((c) => ({ ...c, [k]: v }));
  const canSubmit = Boolean(
    (customer.first_name || "").trim() &&
    (customer.last_name || "").trim() &&
    (customer.email || "").trim() &&
    (customer.phone || "").trim()
  );

  return (
    <div className="space-y-5" data-testid="contact-capture-step">
      <div className="rounded-2xl border-4 border-[var(--ink)] bg-white p-4 md:p-6 shadow-[6px_6px_0_var(--panini-red)]">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="max-w-xl">
            <div className="kicker kicker-red">Step 2</div>
            <h2 className="h-display text-xl md:text-2xl mt-1">Your quote is ready.</h2>
            <p className="text-[var(--smoke)] text-xs md:text-sm mt-1.5 leading-relaxed">
              Tell us who this quote is for and we&apos;ll reveal the full breakdown on the next screen.
              We use this to save your quote and follow up if you need help.
              {collector ? " We prefilled the details from your account — edit anything if it&rsquo;s out of date." : ""}
            </p>
          </div>
          <div className="rounded-full border-2 border-[var(--panini-yellow)] bg-[var(--panini-yellow)]/20 px-3 py-1 text-[10px] md:text-xs font-bold uppercase tracking-widest text-[var(--ink)] flex items-center gap-1.5">
            <ShieldCheck size={12} /> No spam · Never shared
          </div>
        </div>

        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest font-bold text-[var(--ink)]">
              First name <span className="text-[var(--panini-red)]">*</span>
            </span>
            <input
              type="text"
              value={customer.first_name || ""}
              onChange={(e) => setField("first_name", e.target.value)}
              maxLength={100}
              autoComplete="given-name"
              className="mt-1 w-full border-2 border-[var(--ink)] rounded-xl p-2.5 text-sm focus:outline-none focus:ring-4 focus:ring-[var(--panini-yellow)]"
              placeholder="e.g. Alex"
              data-testid="contact-first-name"
            />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest font-bold text-[var(--ink)]">
              Last name <span className="text-[var(--panini-red)]">*</span>
            </span>
            <input
              type="text"
              value={customer.last_name || ""}
              onChange={(e) => setField("last_name", e.target.value)}
              maxLength={100}
              autoComplete="family-name"
              className="mt-1 w-full border-2 border-[var(--ink)] rounded-xl p-2.5 text-sm focus:outline-none focus:ring-4 focus:ring-[var(--panini-yellow)]"
              placeholder="e.g. Rivera"
              data-testid="contact-last-name"
            />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest font-bold text-[var(--ink)]">
              Email <span className="text-[var(--panini-red)]">*</span>
            </span>
            <input
              type="email"
              value={customer.email || ""}
              onChange={(e) => setField("email", e.target.value)}
              maxLength={200}
              autoComplete="email"
              className="mt-1 w-full border-2 border-[var(--ink)] rounded-xl p-2.5 text-sm focus:outline-none focus:ring-4 focus:ring-[var(--panini-yellow)]"
              placeholder="you@example.com"
              data-testid="contact-email"
            />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest font-bold text-[var(--ink)]">
              Phone <span className="text-[var(--panini-red)]">*</span>
            </span>
            <input
              type="tel"
              value={customer.phone || ""}
              onChange={(e) => setField("phone", e.target.value)}
              maxLength={40}
              autoComplete="tel"
              className="mt-1 w-full border-2 border-[var(--ink)] rounded-xl p-2.5 text-sm focus:outline-none focus:ring-4 focus:ring-[var(--panini-yellow)]"
              placeholder="+1 305 555 0100"
              data-testid="contact-phone"
            />
          </label>
        </div>

        <label className="mt-5 flex items-start gap-2.5 cursor-pointer select-none" data-testid="contact-newsletter-label">
          <input
            type="checkbox"
            checked={!!newsletterOptIn}
            onChange={(e) => setNewsletterOptIn(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-[var(--panini-red)]"
            data-testid="contact-newsletter"
          />
          <span className="text-sm text-[var(--smoke)] leading-snug">
            <span className="text-[var(--ink)] font-semibold">Send me collector tips and rare-sticker alerts.</span>{" "}
            Optional — you can unsubscribe anytime and it won&apos;t affect your quote.
          </span>
        </label>

        <p className="mt-4 text-[11px] text-[var(--smoke)] leading-relaxed">
          By continuing you agree we may contact you about this quote. We&apos;ll never share your
          details with anyone else.
        </p>

        <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
          <button
            type="button"
            onClick={onBack}
            className="btn-ghost text-sm"
            data-testid="contact-back"
          >
            <ArrowLeft size={16} /> Back
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={busy || !canSubmit}
            className="btn-red text-sm md:text-base"
            data-testid="contact-submit"
          >
            {busy ? <><Loader2 size={16} className="animate-spin" /> Saving…</> :
                    <><Wand2 size={16} /> Show My Quote <ArrowRight size={16} /></>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  Step 3 — Review Quote (transparent categorized breakdown)
// ─────────────────────────────────────────────────────────────
function StepReview({ quote, parsed, variant, onBack, onNext, deselected, onToggleSelection, onRestoreAll, onUncheckAll, onCheckOutOfStock, onUncheckOutOfStock, onRestoreCategory, onSelectCategory, onUnselectCategory, sourceQuoteId, selectionBusy, originalLines, delivery, feedbackAuth, adminAdjustments }) {
  const totals = quote.totals || {};
  const flags = quote.flags || {};
  const minInfo = totals.regular_minimum_info || null;

  // Phase 4 — the ORIGINAL priced line list is the source of truth for
  // what to render (so unchecked rows still appear as strike-through and
  // can be restored). The live `quote` object provides authoritative
  // totals + availability summary that recompute after each toggle.
  const linesForRender = useMemo(
    () => (originalLines && originalLines.length ? originalLines : (quote.lines || [])),
    [originalLines, quote.lines]
  );

  // Phase 4 — selection API passed down to QuoteCategorySummary so each
  // priced line row renders a checkbox tied to the parent state.
  const isSelected = useCallback(
    (code) => !(deselected && deselected.has(code)),
    [deselected]
  );
  const selectionApi = useMemo(
    () => ({ isSelected, onToggle: onToggleSelection,
             onRestoreCategory,
             onSelectCategory,
             onUnselectCategory }),
    [isSelected, onToggleSelection, onRestoreCategory,
     onSelectCategory, onUnselectCategory]
  );

  // Phase 5 · Group-action toolbar state.
  const oosCount = useMemo(() => {
    return (originalLines || []).filter(
      (l) => l?.availability?.availability_status === "out_of_stock",
    ).length;
  }, [originalLines]);
  const anyDeselected = deselected && deselected.size > 0;
  const totalPricedLines = (originalLines || []).length;
  const allSelected = totalPricedLines > 0 && !anyDeselected;
  const noneSelected = totalPricedLines > 0 && deselected.size === totalPricedLines;

  // Phase 4 — computed removed count in stickers (respects qty).
  const removedCount = useMemo(() => {
    if (!deselected || deselected.size === 0) return 0;
    let n = 0;
    for (const ln of linesForRender) {
      if (deselected.has(ln.code)) n += Number(ln.qty || 0);
    }
    return n;
  }, [linesForRender, deselected]);

  const allRemoved = removedCount > 0 && (totals.sticker_count || 0) === 0;

  // ── Copy-invoice deps: payment instructions + business address, fetched once
  // when the user reaches Review. Falls back to sensible defaults if anything
  // is missing so the button ALWAYS produces a valid DM-ready invoice.
  const [invoiceMeta, setInvoiceMeta] = useState({
    zelle_phone:      "",
    zelle_name:       "",
    pickup_city:      "Miami Gardens",
  });
  useEffect(() => {
    let alive = true;
    Promise.allSettled([
      axios.get(`${API}/orders-payment-instructions`),
      axios.get(`${API}/settings/public`),
    ]).then(([pay, set]) => {
      if (!alive) return;
      const payData  = pay.status === "fulfilled" ? pay.value.data  : {};
      const setData  = set.status === "fulfilled" ? set.value.data  : {};
      const phone    = (payData.zelle_recipient_phone || "").replace(/^\+1[-\s]?/, "").trim();
      const name     = payData.zelle_recipient_name || "";
      // Extract "Miami Gardens" from `business_address` (line containing state code).
      let pickup = "Miami Gardens";
      const addr = setData.business_address || "";
      const m = addr.match(/([A-Za-z\u00C0-\u024F .'-]+),\s*[A-Z]{2}\s+\d{5}/);
      if (m) pickup = m[1].trim();
      setInvoiceMeta({ zelle_phone: phone, zelle_name: name, pickup_city: pickup });
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const grouped = useMemo(
    () => groupLinesByCategory(linesForRender, isSelected, variant),
    [linesForRender, isSelected, variant]
  );

  // Which of the currently hard-to-source countries appear in this
  // quote? Purely for display — pricing is untouched (Msg-892).
  const scarceCountriesInQuote = useMemo(() => {
    const hit = new Set();
    for (const ln of linesForRender) {
      if (!isSelected(ln.code)) continue;
      const prefix = String(ln.code || "").split("-")[0].toUpperCase();
      if (HARDER_TO_SOURCE_COUNTRIES.has(prefix)) hit.add(prefix);
    }
    // Deterministic sort so the notice reads the same across renders.
    return Array.from(hit).sort();
  }, [linesForRender, isSelected]);

  // ── Unresolved entries (CR-3) ────────────────────────────────────
  // Combines parser unknowns (bad tokens like ARGABC) + quote unknowns
  // (parsed OK but not in the Master DB, like RUS-5, ARG-99). Each
  // entry is classified so the customer knows WHY it failed. Never
  // priced, never guessed, always visible.
  // F6 (owner directive 2026-02-20) · The unresolved-entry gate must
  // represent ONLY the current quote's variant. `parsed` and `quote`
  // are always set together by `parseAndQuote()` for the same
  // variant (F4 clears both on a variant switch), so this memo will
  // never mix cross-variant unknowns. We also dedupe by canonical
  // code / display so a code cannot double-represent (once from the
  // parser, once from the engine) — that pattern was one of the
  // symptoms of the QT-26-35HFW bug.
  const unresolvedEntries = useMemo(() => {
    const rows = [];
    const seen = new Set();
    const push = (row) => {
      const key = String(row.display || "").trim().toUpperCase()
        + "|" + String(row.reason_key || "");
      if (seen.has(key)) return;
      seen.add(key);
      rows.push(row);
    };
    // Parser-side unknowns preserve the customer's exact raw token.
    for (const u of (parsed?.unknowns || [])) {
      push({
        source: "parser",
        display: u.raw_token || "(unknown)",
        reason_key: u.reason || "unrecognized_format",
      });
    }
    // Pricing-engine unknowns already have a normalized code + prefix,
    // so we can distinguish "country not recognized" from "sticker
    // number not found". The `code` field carries the parser's canonical
    // form (e.g. RUS-5) which is what the customer typed.
    for (const u of (quote?.unknowns || [])) {
      const canonical = u.canonical_code || u.code || "";
      const prefix = String(canonical).match(/^[A-Z]+/)?.[0] || "";
      const knownPrefix = KNOWN_MASTER_PREFIXES.has(prefix);
      push({
        source: "quote",
        display: u.code || canonical || "(unknown)",
        reason_key: knownPrefix ? "sticker_number_not_found" : "unknown_country",
      });
    }
    return rows;
  }, [parsed, quote]);
  const hasUnresolved = unresolvedEntries.length > 0;

  // ── Build the DM-ready invoice text (Phase 5 redesign).
  //   • Every per-sticker line inside a pricing bucket is included, but
  //     only when currently checked (`isSelected`).
  //   • Only the selected delivery method (Pickup or Shipping) is shown.
  //   • Quote ID printed at the bottom for cross-reference.
  const invoiceGroups = useMemo(
    () => grouped.map((g) => ({ ...g, _isSelected: isSelected })),
    [grouped, isSelected]
  );
  const shippingInfo = totals?.shipping || null;
  // Phase B · Selected sold-out codes (deduplicated). Only codes that
  // remain SELECTED — if the collector clicked Remove All Sold-Out
  // Stickers, this list is empty and the AVAILABILITY section is not
  // emitted.
  const selectedSoldOutCodes = useMemo(() => {
    const out = new Set();
    for (const l of originalLines || []) {
      if (l?.availability?.availability_status === "out_of_stock"
          && !deselected.has(l.code)) {
        out.add(l.code);
      }
    }
    return Array.from(out);
  }, [originalLines, deselected]);
  const invoiceText = useMemo(
    () => formatQuoteForClipboard({
      totals,
      groups: invoiceGroups,
      meta: invoiceMeta,
      deliveryCode: delivery,
      shippingLabel: shippingInfo?.label || "UPS Ground",
      shippingCost:  shippingInfo?.cost  || 0,
      sourceQuoteId,
      productName: ALBUM_DISPLAY,
      soldOutCodes: selectedSoldOutCodes,
      variant,
    }),
    [totals, invoiceGroups, invoiceMeta, delivery, shippingInfo, sourceQuoteId,
     selectedSoldOutCodes, variant],
  );

  return (
    <div data-testid="review-step">
      {/* Zero-result guard — original quote never had any priced items. */}
      {(!linesForRender || linesForRender.length === 0) ? (
        <ZeroResultState onBack={onBack} unknowns={parsed.unknowns || []} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: quote content */}
          <div className="lg:col-span-2 space-y-4">
            {/* Header */}
            <div className="rounded-2xl border-4 border-[var(--ink)] bg-[var(--panini-yellow)] p-5 md:p-6 shadow-[6px_6px_0_var(--panini-red)]">
              <div className="kicker kicker-red">Step 2 · Your quote</div>
              <h2 className="h-display text-2xl md:text-3xl mt-1">Order Summary</h2>
              <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
                <span className="inline-flex items-center gap-2 rounded-full bg-white border-2 border-[var(--ink)] px-3 py-1 font-bold" data-testid="review-total-count">
                  <ClipboardList size={14} /> {totals.sticker_count || 0} stickers total
                </span>
                <span className="hidden md:inline-flex items-center gap-2 rounded-full bg-white border-2 border-[var(--ink)] px-3 py-1 font-bold">
                  {ALBUM_DISPLAY}
                </span>
                {/* P3.2 · Prominent variant badge for non-white
                    quotes so the customer sees exactly which parallel
                    they're buying. White stays clean (see below). */}
                <ReviewVariantBadge variant={variant} />
              </div>
            </div>

            {/* Availability information — reassuring, not blocking. */}
            <AvailabilityNotice />

            {/* Phase 3 · Live quote-level availability banner driven by
                the backend availability totals. Additive; never blocks
                checkout, never alters price. */}
            <QuoteAvailabilityBanner availability={quote.availability} />

            {/* Phase B · Actionable sold-out summary + Remove / Restore
                controls. Only renders when the original request
                contains at least one sold-out sticker. Reuses the
                existing uncheckOutOfStock / checkOutOfStock selection
                logic — no new pricing math. */}
            <SoldOutActionPanel
              originalLines={originalLines}
              deselected={deselected}
              onRemove={onUncheckOutOfStock}
              onRestore={onCheckOutOfStock}
            />

            {/* Hard-to-source contextual note — only when applicable. */}
            {scarceCountriesInQuote.length > 0 && (
              <ScarceCountryNotice countries={scarceCountriesInQuote} />
            )}

            {/* Under-35 explanation */}
            {flags.regular_minimum_applied && minInfo && (
              <div className="rounded-2xl border-4 border-[var(--panini-red)] bg-[#fff8f0] p-4 md:p-5 flex items-start gap-3" data-testid="review-under-35-note">
                <div className="shrink-0 mt-0.5 text-[var(--panini-red)]"><Info size={20} /></div>
                <div>
                  <div className="font-display text-base md:text-lg text-[var(--ink)]">
                    Under {minInfo.under_qty} stickers — Under 35 prices apply
                  </div>
                  <p className="text-xs md:text-sm text-[var(--ink)]/80 mt-1 leading-relaxed">
                    {minInfo.message}
                  </p>
                </div>
              </div>
            )}

            {/* Collapsible sections — grouped by customer-friendly
                category using backend-supplied `display_category`.
                FWC → Team Badges → Special Players → Coca-Cola →
                $2 → $1 → Regular → Other. Categories with ≤ 5 items
                default open. Pricing is 100% backend-driven — this
                is a presentation change only. */}
            {/* Phase 5 · Group actions bar — quote-wide bulk toggles. */}
            <GroupActionsBar
              disabled={selectionBusy}
              allSelected={allSelected}
              noneSelected={noneSelected}
              oosCount={oosCount}
              anyDeselected={anyDeselected}
              onCheckAll={onRestoreAll}
              onUncheckAll={onUncheckAll}
              onCheckOOS={onCheckOutOfStock}
              onUncheckOOS={onUncheckOutOfStock}
            />

            {/* Phase 4 · Selection summary row — appears only when the
                customer has un-checked at least one sticker. Restore All
                brings every original sticker back in one click. */}
            {removedCount > 0 && (
              <RemovedSummary
                count={removedCount}
                onRestoreAll={onRestoreAll}
                allRemoved={allRemoved}
                busy={selectionBusy}
              />
            )}

            <QuoteCategorySummary groups={grouped} variant="customer" orderVariant={variant} selectionApi={selectionApi} />

            {/* Unresolved entries — CR-3: must never disappear silently.
                Merges parser-side unknowns (unrecognised tokens) and
                pricing-engine unknowns (parsed but not in the Master
                Sticker Database). Submission is blocked while ANY
                unresolved entry remains. Prices are never invented. */}
            {unresolvedEntries.length > 0 && (
              <UnresolvedEntriesNotice
                entries={unresolvedEntries}
                onBack={onBack}
              />
            )}

            {/* Back button (main-column) */}
            <div className="pt-2 flex items-center justify-between gap-3 flex-wrap">
              <button onClick={onBack} className="btn-ghost text-sm" data-testid="review-back">
                <ArrowLeft size={14} /> Edit list
              </button>
              {/* Phase 4 · Quote Help — open IG DM or WhatsApp with a
                  pre-filled message referencing this quote so Gil can
                  answer questions before checkout. */}
              <QuoteHelp sourceQuoteId={sourceQuoteId} />
            </div>
          </div>

          {/* Right: sticky summary (desktop) */}
          <StickySummary
            totals={totals}
            adminAdjustments={adminAdjustments}
            onNext={onNext}
            disabled={hasUnresolved || allRemoved || selectionBusy}
            disabledMessage={
              hasUnresolved
                ? "Please correct or remove the unrecognized entries before submitting your order."
                : allRemoved
                ? "You've removed every sticker. Restore at least one to continue."
                : selectionBusy
                ? "Updating your quote…"
                : ""
            }
            invoiceText={invoiceText}
            selectionBusy={selectionBusy}
          />
        </div>
      )}

      {/* Mobile sticky bottom bar (visible only on <lg screens when quote has lines) */}
      {linesForRender && linesForRender.length > 0 && (
        <MobileStickyBar
          totals={totals}
          adminAdjustments={adminAdjustments}
          onNext={onNext}
          disabled={hasUnresolved || allRemoved || selectionBusy}
          disabledMessage={
            hasUnresolved
              ? "Please correct or remove the unrecognized entries before submitting your order."
              : allRemoved
              ? "You've removed every sticker. Restore at least one to continue."
              : selectionBusy
              ? "Updating your quote…"
              : ""
          }
          invoiceText={invoiceText}
          selectionBusy={selectionBusy}
        />
      )}

      {feedbackAuth?.publicId && (
        <AbandonmentFeedback
          publicId={feedbackAuth.publicId}
          resumeToken={feedbackAuth.resumeToken}
          requestId={feedbackAuth.requestId}
          sessionId={feedbackAuth.sessionId}
          source={feedbackAuth.source}
          existingFeedback={feedbackAuth.existingFeedback}
        />
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
//  Phase 4 · Removed-items summary + Restore All
//  Appears above the category list when the customer has un-checked
//  at least one sticker. Instantly restores the original set.

// ─────────────────────────────────────────────────────────────
//  Phase 5 · Group Actions bar
//  Quote-wide bulk toggles rendered directly above the category list.
//  Every action is idempotent and disabled during the debounced
//  server re-quote so rapid clicks can't queue up stale requests.
// ─────────────────────────────────────────────────────────────
function GroupActionsBar({
  disabled, allSelected, noneSelected, oosCount, anyDeselected,
  onCheckAll, onUncheckAll, onCheckOOS, onUncheckOOS,
}) {
  const btn =
    "inline-flex items-center gap-1.5 rounded-full border-2 border-[var(--ink)] bg-white px-3 py-1.5 text-[11px] md:text-xs font-bold uppercase tracking-widest hover:bg-[var(--panini-yellow-soft)] transition disabled:opacity-40 disabled:cursor-not-allowed";
  return (
    <div
      className="rounded-2xl border-2 border-[var(--stone)] bg-white p-3 flex flex-wrap items-center gap-2"
      data-testid="group-actions-bar"
      role="toolbar"
      aria-label="Bulk selection controls"
    >
      <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)] mr-1">
        Quick edit
      </span>
      <button
        type="button"
        onClick={onCheckAll}
        disabled={disabled || allSelected}
        className={btn}
        data-testid="group-action-check-all"
        title="Restore every sticker to your order"
      >
        <CheckSquare size={12} /> Check All
      </button>
      <button
        type="button"
        onClick={onUncheckAll}
        disabled={disabled || noneSelected}
        className={btn}
        data-testid="group-action-uncheck-all"
        title="Remove every sticker from your order"
      >
        <Square size={12} /> Uncheck All
      </button>
      <span aria-hidden="true" className="mx-1 text-[var(--stone)]">|</span>
      <button
        type="button"
        onClick={onCheckOOS}
        disabled={disabled || oosCount === 0}
        className={btn}
        data-testid="group-action-check-oos"
        title={oosCount ? `Restore all ${oosCount} out-of-stock sticker${oosCount === 1 ? "" : "s"}` : "No out-of-stock stickers in this quote"}
      >
        <CheckSquare size={12} /> Check Out of Stock{oosCount ? ` (${oosCount})` : ""}
      </button>
      <button
        type="button"
        onClick={onUncheckOOS}
        disabled={disabled || oosCount === 0}
        className={btn}
        data-testid="group-action-uncheck-oos"
        title={oosCount ? `Remove all ${oosCount} out-of-stock sticker${oosCount === 1 ? "" : "s"}` : "No out-of-stock stickers in this quote"}
      >
        <Square size={12} /> Uncheck Out of Stock{oosCount ? ` (${oosCount})` : ""}
      </button>
      {anyDeselected && (
        <span className="ml-auto text-[10px] text-[var(--smoke)] font-mono" data-testid="group-actions-hint">
          Tip: use &quot;Restore&quot; on any category header to bring back just that group.
        </span>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
function RemovedSummary({ count, onRestoreAll, allRemoved, busy }) {
  return (
    <div
      className={
        "rounded-2xl border-2 p-4 md:p-5 flex items-start gap-3 " +
        (allRemoved
          ? "border-red-700 bg-red-50"
          : "border-slate-500 bg-slate-50")
      }
      role="status"
      data-testid="removed-summary"
    >
      <div className={"shrink-0 mt-0.5 " + (allRemoved ? "text-red-700" : "text-slate-700")}>
        <Info size={20} />
      </div>
      <div className="min-w-0 flex-1">
        <div className={"font-display text-base md:text-lg " + (allRemoved ? "text-red-900" : "text-slate-900")}>
          You removed {count} sticker{count === 1 ? "" : "s"}
          <span className="ml-1 font-normal text-xs md:text-sm text-slate-600" data-testid="removed-summary-count">
            ({count} of your original quote)
          </span>
        </div>
        <p className={"text-xs md:text-sm mt-1 leading-relaxed " + (allRemoved ? "text-red-900/80" : "text-slate-700")}>
          {allRemoved
            ? "You've removed every sticker. Restore at least one to continue to checkout."
            : "Your subtotal, tax and total have been updated. Restore All puts every original sticker back."}
        </p>
      </div>
      <button
        type="button"
        onClick={onRestoreAll}
        disabled={busy}
        className="shrink-0 inline-flex items-center gap-2 rounded-full border-2 border-[var(--ink)] bg-white px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-[var(--panini-yellow-soft)] transition disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="removed-summary-restore-all"
        aria-label="Restore all removed stickers"
      >
        <RotateCcw size={14} /> Restore All
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  Phase 4 · Quote Help — opens Instagram DM or WhatsApp with a
//  pre-filled message referencing this quote's ID so Gil can answer
//  the customer's question without asking for context first.
// ─────────────────────────────────────────────────────────────
const IG_HANDLE   = "gilmaakaroun";
const WHATSAPP_E164 = "17865756919"; // dedicated CollectorUp WhatsApp business line

function _quoteRefLabel(qid) {
  if (!qid) return null;
  // Present customer-friendly form: strip the QT- prefix for brevity when
  // rendering as a chip, but keep the full id in the DM text.
  return qid.startsWith("QT-") ? qid : `QT-${qid}`;
}

function QuoteHelp({ sourceQuoteId }) {
  const ref = _quoteRefLabel(sourceQuoteId);
  const waMsg = ref
    ? `Hi Gil! I have a question about my quote ${ref}. Could you help me before I check out?`
    : "Hi Gil! I have a question about my missing-sticker quote before I check out. Could you help me?";
  const igMsg = waMsg;
  const waHref = `https://wa.me/${WHATSAPP_E164}?text=${encodeURIComponent(waMsg)}`;
  // Instagram deep-link cannot pre-fill a DM body — we open the profile
  // and also copy the ID to the clipboard so the customer can paste it.
  const igHref = `https://ig.me/m/${IG_HANDLE}`;

  const openIG = async (e) => {
    e.preventDefault();
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(igMsg);
        toast.success(ref ? `Quote ${ref} copied — paste it in Instagram DM.` : "Message copied — paste it in Instagram DM.");
      }
    } catch (_e) { /* ignore — still open IG */ }
    window.open(igHref, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      className="inline-flex items-center gap-2 flex-wrap"
      data-testid="quote-help"
      data-quote-ref={ref || ""}
    >
      <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-[var(--smoke)]">
        <HelpCircle size={14} /> Question?
      </span>
      <a
        href={waHref}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => { try { track(EVENT.MISSING_QUOTE_STARTED, { via: "wa_help", quote_ref: ref || null }); } catch (_e) { /* analytics best-effort */ } }}
        className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--ink)] bg-white px-3 py-1.5 text-xs font-bold hover:bg-[var(--panini-yellow-soft)] transition"
        data-testid="quote-help-whatsapp"
      >
        <MessageCircle size={14} /> WhatsApp
      </a>
      <button
        type="button"
        onClick={openIG}
        className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--ink)] bg-white px-3 py-1.5 text-xs font-bold hover:bg-[var(--panini-yellow-soft)] transition"
        data-testid="quote-help-instagram"
      >
        <Instagram size={14} /> Instagram DM
      </button>
      {ref && (
        <span
          className="inline-flex items-center gap-1 rounded-full bg-slate-100 border border-slate-300 px-2 py-0.5 font-mono text-[10px] text-slate-700"
          data-testid="quote-help-ref"
          title="This ID will be included with your message"
        >
          {ref}
        </span>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
//  Unresolved entries notice (CR-3) — every entry the customer typed
//  that we could NOT confidently price is surfaced here with a plain
//  human reason. Order submission is blocked while this list is
//  non-empty. Pricing is never invented; unresolved entries are
//  excluded from the total.
// ─────────────────────────────────────────────────────────────
const UNRESOLVED_REASON_LABELS = {
  unknown_country:                "Country code not recognized",
  sticker_number_not_found:       "Sticker number not found",
  outside_manifest:               "Sticker number not found",
  unrecognized_format:            "Invalid sticker format",
  conflicting_country_identifiers:"Invalid sticker format",
  missing_country_context:        "Invalid sticker format",
  // Phase 5 — clearer, per-cause reasons emitted when the parser runs
  // with `validate_master_db=True`.
  sticker_zero_invalid:           "Sticker number cannot be 0",
  sticker_out_of_range:           "Sticker number outside valid range",
  ambiguous_compact_number:       "Could not safely determine what this number means",
  reversed_range:                 "Range is backwards (e.g. 15-3)",
  range_too_wide:                 "Range is too wide to expand safely",
};

function UnresolvedEntriesNotice({ entries, onBack }) {
  return (
    <div
      className="rounded-2xl border-4 border-red-700 bg-red-50 p-4 md:p-5"
      data-testid="review-unresolved-entries"
      role="alert"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle size={20} className="text-red-700 mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="font-display text-base md:text-lg text-red-900">
            Please review these entries
          </div>
          <p className="text-xs md:text-sm text-red-900/80 mt-1 leading-relaxed">
            We couldn&apos;t recognize the entries below. Please verify the
            country code, sticker number, or format before placing your order.
            These entries are <b>not</b> included in your total.
          </p>
          <ul className="mt-3 space-y-1.5" data-testid="review-unresolved-list">
            {entries.map((e, idx) => {
              const reason = UNRESOLVED_REASON_LABELS[e.reason_key] || "Sticker not found";
              return (
                <li
                  key={`unresolved-${idx}`}
                  data-testid={`review-unresolved-${idx}`}
                  className="text-sm text-red-900 bg-white border-2 border-red-300 rounded-lg px-3 py-2 flex items-center gap-2 flex-wrap"
                >
                  <span className="font-mono font-bold break-all">{e.display}</span>
                  <span className="text-red-700">—</span>
                  <span className="text-red-800">{reason}</span>
                </li>
              );
            })}
          </ul>
          <div className="mt-3 text-xs md:text-sm text-red-900 font-bold">
            Please correct or remove the unrecognized entries before submitting your order.
          </div>
          <button
            type="button"
            onClick={onBack}
            className="mt-3 inline-flex items-center gap-2 rounded-full bg-red-700 hover:bg-red-800 text-white text-xs md:text-sm font-bold uppercase tracking-widest px-4 py-2"
            data-testid="review-unresolved-edit-list"
          >
            <ArrowLeft size={14} /> Edit my list
          </button>
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
//  Availability notice — reassuring info box shown on every Review
//  Quote render. Does NOT block submission; does NOT re-price
//  anything. Purely informational.
// ─────────────────────────────────────────────────────────────
function AvailabilityNotice() {
  return (
    <div
      className="rounded-2xl border-2 border-blue-300 bg-blue-50 p-4 md:p-5 flex items-start gap-3"
      data-testid="review-availability-notice"
      role="note"
    >
      <div className="shrink-0 mt-0.5 text-blue-700">
        <Info size={20} />
      </div>
      <div className="min-w-0">
        <div className="font-display text-base md:text-lg text-blue-900">
          Availability Information
        </div>
        <p className="text-xs md:text-sm text-blue-900/80 mt-1 leading-relaxed">
          Most stickers are currently available. Because we manage more than
          10,000 individual stickers and receive new batches several times each
          day, a small number may require additional sourcing. After you place
          your order, we&apos;ll email you a complete availability update showing
          which stickers are ready and which may take a little longer. We&apos;ll
          continue working on your order until it is complete.
        </p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  Harder-to-source note — displayed ONLY when the quote contains
//  one or more stickers from the scarce-country list. NEVER touches
//  pricing (spreadsheet remains source of truth per Msg-892).
// ─────────────────────────────────────────────────────────────
function QuoteAvailabilityBanner({ availability }) {
  if (!availability) return null;
  const {
    has_out_of_stock, has_uninitialized_inventory,
    in_stock_count, out_of_stock_count, not_initialized_count,
  } = availability;

  // Priority: out-of-stock > uninitialized > all-in-stock.
  if (has_out_of_stock) {
    return (
      <div
        className="rounded-2xl border-2 border-amber-800 bg-amber-50 p-4 md:p-5 flex items-start gap-3"
        role="alert"
        data-testid="quote-availability-banner-out-of-stock"
      >
        <div className="shrink-0 mt-0.5 text-amber-800"><Info size={20} /></div>
        <div className="min-w-0">
          <div className="font-display text-base md:text-lg text-amber-900">
            {out_of_stock_count} sticker{out_of_stock_count === 1 ? "" : "s"} currently out of stock
          </div>
          <p className="text-xs md:text-sm text-amber-900/90 mt-1 leading-relaxed">
            Some stickers in this quote are currently out of stock. You will be able to keep
            them on your order and wait for fulfillment, or remove them before checkout.
          </p>
        </div>
      </div>
    );
  }
  if (has_uninitialized_inventory) {
    return (
      <div
        className="rounded-2xl border-2 border-neutral-400 bg-neutral-50 p-4 md:p-5 flex items-start gap-3"
        role="status"
        data-testid="quote-availability-banner-not-initialized"
      >
        <div className="shrink-0 mt-0.5 text-neutral-600"><Info size={20} /></div>
        <div className="min-w-0">
          <div className="font-display text-base md:text-lg text-neutral-800">
            Availability check pending
          </div>
          <p className="text-xs md:text-sm text-neutral-700 mt-1 leading-relaxed">
            Availability has not yet been confirmed for some stickers in this quote.
            {not_initialized_count > 0 ? ` (${not_initialized_count} sticker${not_initialized_count === 1 ? "" : "s"})` : ""}
          </p>
        </div>
      </div>
    );
  }
  if ((in_stock_count || 0) > 0) {
    return (
      <div
        className="rounded-2xl border-2 border-emerald-800 bg-emerald-50 p-4 md:p-5 flex items-start gap-3"
        role="status"
        data-testid="quote-availability-banner-all-in-stock"
      >
        <div className="shrink-0 mt-0.5 text-emerald-800"><Info size={20} /></div>
        <div className="min-w-0">
          <div className="font-display text-base md:text-lg text-emerald-900">
            All quoted stickers are currently marked in stock
          </div>
        </div>
      </div>
    );
  }
  return null;
}

// ─────────────────────────────────────────────────────────────
//  Phase B · Actionable sold-out summary + Remove / Restore.
//
//  Reads the ORIGINAL requested lines (frozen at quote time) and
//  the current `deselected` Set to produce a self-contained panel
//  with:
//    · Total requested sticker count
//    · Selected in-stock count
//    · Selected sold-out count
//    · Deduplicated sold-out code list
//    · "Remove All Sold-Out Stickers" / "Restore Sold-Out Stickers"
//      buttons wired to the existing uncheckOutOfStock /
//      checkOutOfStock handlers (no new pricing math).
//
//  Rules
//    · Never renders when the original request contains zero
//      sold-out lines — the pre-existing banner already covers the
//      all-in-stock state.
//    · Never touches price / tax / total / shipping — those live
//      inside the parent's existing quote recalculation pipeline.
//    · Never claims a waitlist exists (Phase C).
// ─────────────────────────────────────────────────────────────
function SoldOutActionPanel({ originalLines, deselected, onRemove, onRestore }) {
  const lines = originalLines || [];
  const soldOutLines = lines.filter(
    (l) => l?.availability?.availability_status === "out_of_stock"
  );
  if (soldOutLines.length === 0) return null;

  const totalRequested = lines.reduce((n, l) => n + Number(l.qty || 0), 0);
  const inStockLines = lines.filter(
    (l) => l?.availability?.availability_status !== "out_of_stock"
  );
  const selectedInStockCount = inStockLines
    .filter((l) => !deselected.has(l.code))
    .reduce((n, l) => n + Number(l.qty || 0), 0);
  const selectedSoldOutCount = soldOutLines
    .filter((l) => !deselected.has(l.code))
    .reduce((n, l) => n + Number(l.qty || 0), 0);

  // Deduplicated sold-out codes for the compact summary list.
  const uniqueSoldOutCodes = Array.from(
    new Set(soldOutLines.map((l) => l.code))
  );
  const allSoldOutRemoved = selectedSoldOutCount === 0;
  const allSoldOutKept = selectedSoldOutCount === soldOutLines
    .reduce((n, l) => n + Number(l.qty || 0), 0);

  return (
    <div
      className="rounded-2xl border-2 border-[var(--ink)] bg-white p-4 md:p-5"
      data-testid="soldout-panel"
      data-state={allSoldOutRemoved ? "removed" : allSoldOutKept ? "kept" : "partial"}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[var(--panini-red)]">
            Availability
          </div>
          <div className="font-display text-lg md:text-xl mt-0.5"
               data-testid="soldout-panel-heading">
            {allSoldOutRemoved
              ? "Sold-out stickers removed"
              : "Some stickers in your order are currently sold out"}
          </div>
        </div>
      </div>

      <dl className="mt-3 grid grid-cols-3 gap-3 text-center"
          data-testid="soldout-panel-metrics">
        <div>
          <dt className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">Requested</dt>
          <dd className="font-mono text-xl text-[var(--ink)]"
              data-testid="soldout-metric-requested">{totalRequested}</dd>
        </div>
        <div>
          <dt className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">In stock</dt>
          <dd className="font-mono text-xl text-emerald-700"
              data-testid="soldout-metric-in-stock">{selectedInStockCount}</dd>
        </div>
        <div>
          <dt className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">Sold out</dt>
          <dd className="font-mono text-xl text-amber-800"
              data-testid="soldout-metric-sold-out">{selectedSoldOutCount}</dd>
        </div>
      </dl>

      {!allSoldOutRemoved && (
        <div className="mt-3 text-sm text-[var(--ink)]/90">
          Some stickers in your order are currently sold out. You can remove the
          sold-out stickers and continue with the available order, or keep the
          complete order and wait while we locate them.
        </div>
      )}

      {!allSoldOutRemoved && (
        <div className="mt-3 rounded-xl bg-neutral-50 border border-[var(--stone)] p-3"
             data-testid="soldout-panel-code-list">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)] mb-1">
            Currently sold out
          </div>
          <div className="font-mono text-sm text-[var(--ink)] flex flex-wrap gap-x-3 gap-y-1"
               data-testid="soldout-panel-codes">
            {uniqueSoldOutCodes.map((c) => (
              <span key={c} data-testid={`soldout-code-${c}`}>{c}</span>
            ))}
          </div>
        </div>
      )}

      {allSoldOutKept && (
        <div className="mt-3 text-xs md:text-sm text-[var(--ink)]/90 border-l-4 border-amber-500 pl-3"
             data-testid="soldout-panel-keep-notice">
          <strong className="text-amber-900">Complete request includes sold-out stickers.</strong>{" "}
          You may continue with the full request. We will review the unavailable
          stickers and contact you regarding fulfillment.
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {!allSoldOutRemoved ? (
          <button
            type="button"
            onClick={onRemove}
            className="inline-flex items-center gap-1.5 rounded-full border-2 border-[var(--ink)] bg-[var(--panini-red)] text-white px-4 py-2 text-sm font-bold uppercase tracking-widest hover:brightness-110"
            data-testid="soldout-btn-remove"
          >
            Remove All Sold-Out Stickers
          </button>
        ) : (
          <button
            type="button"
            onClick={onRestore}
            className="inline-flex items-center gap-1.5 rounded-full border-2 border-[var(--ink)] bg-white text-[var(--ink)] px-4 py-2 text-sm font-bold uppercase tracking-widest hover:bg-[var(--panini-yellow-soft)]"
            data-testid="soldout-btn-restore"
          >
            Restore Sold-Out Stickers
          </button>
        )}
      </div>
    </div>
  );
}

function ScarceCountryNotice({ countries }) {
  const list = (countries || []).join(", ");
  const plural = (countries || []).length > 1;
  return (
    <div
      className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-4 md:p-5 flex items-start gap-3"
      data-testid="review-scarce-notice"
      role="note"
    >
      <div className="shrink-0 mt-0.5">
        <span
          className="inline-flex items-center gap-1 rounded-full border-2 border-amber-700 bg-amber-100 text-amber-900 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest"
          data-testid="review-scarce-badge"
        >
          <AlertTriangle size={10} /> Harder to source
        </span>
      </div>
      <div className="min-w-0">
        <div className="font-display text-sm md:text-base text-amber-900">
          {plural ? (
            <>Your quote includes {list} stickers, which are currently among the hardest to source.</>
          ) : (
            <>{list} stickers are currently among the hardest to source.</>
          )}
        </div>
        <p className="text-xs text-amber-900/80 mt-1 leading-relaxed">
          Their pricing reflects their limited availability and the additional
          work required to locate them. We&apos;ll keep working on your order
          until every sticker we can find has been picked.
        </p>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
//  Invoice generator — Phase 5 redesign.
//  Produces a DM-ready text invoice that closely mirrors the manual
//  invoices Gil sends today. Safe to paste into Instagram DM, WhatsApp,
//  Messenger, SMS, or email with zero editing.
//
//  Rules (locked by owner directive):
//    • Every sticker is listed under its pricing bucket.
//    • Country codes stay grouped together (backend already sorts each
//      bucket numerically → ARG-* → BRA-* → CC-* order preserved).
//    • PAYMENT section is visually emphasised.
//    • ONLY the selected delivery method is shown (Pickup OR Shipping).
//    • Quote ID printed at the bottom for cross-reference.
//    • Pricing calculations are 100% pass-through — every value comes
//      from the backend quote response verbatim.
// ─────────────────────────────────────────────────────────────
// Legacy `buildInvoiceText` + its `_dollar` / `_leaderRow` helpers
// were removed on Copy-Quote clean-plain-text migration. The
// clipboard formatter now lives in `@/lib/formatQuoteForClipboard`
// (imported at the top of this file).


// P3.2 · Customer-facing variant badge for the Review Quote header.
// - Blue / Gold / Red / Orange / Purple render a prominent colored
//   pill so a colored-parallel quote can NEVER be mistaken for White.
// - White uses a compact monochrome identifier — the goal is clarity,
//   not decoration.
const REVIEW_VARIANT_LABELS = {
  white:        "White",
  blue:         "Blue Parallel",
  gold_crumple: "Gold Crumple",
  red:          "Red Parallel",
  orange:       "Orange Parallel",
  purple:       "Purple Parallel",
};
function ReviewVariantBadge({ variant }) {
  const v = String(variant || "white").toLowerCase();
  const label = REVIEW_VARIANT_LABELS[v] || REVIEW_VARIANT_LABELS.white;
  if (v === "white") {
    return (
      <span
        className="inline-flex items-center gap-2 rounded-full bg-white border-2 border-[var(--ink)] px-3 py-1 text-[11px] uppercase tracking-widest font-bold"
        data-testid="review-variant-badge-white"
      >
        <span className="inline-block w-2 h-2 rounded-full bg-neutral-400" />
        {label}
      </span>
    );
  }
  const tone = {
    blue:         "bg-blue-600 text-white border-blue-800",
    gold_crumple: "bg-amber-500 text-black border-amber-700",
    red:          "bg-red-600 text-white border-red-800",
    orange:       "bg-orange-500 text-white border-orange-700",
    purple:       "bg-purple-600 text-white border-purple-800",
  }[v] || "bg-neutral-800 text-white border-black";
  return (
    <span
      className={"inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] uppercase tracking-widest font-black border-2 " + tone}
      data-testid={`review-variant-badge-${v}`}
    >
      {label}
    </span>
  );
}



function CopyInvoiceButton({ text, testid = "copy-invoice", variant = "primary" }) {
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState(false);
  const doCopy = async () => {
    setErr(false);
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for non-secure contexts / older browsers.
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      track(EVENT.COPY_INVOICE_CLICKED, { length: text.length });
      toast.success("Invoice copied — paste it in Instagram DM, WhatsApp, or email.");
      setTimeout(() => setCopied(false), 2200);
    } catch (e) {
      setErr(true);
      toast.error("Couldn't copy automatically. Long-press the preview to copy manually.");
    }
  };
  const base = variant === "ghost"
    ? "inline-flex items-center gap-2 rounded-full border-2 border-[var(--ink)] bg-white px-3 py-1.5 text-xs font-bold uppercase tracking-widest hover:bg-[var(--panini-yellow-soft)] transition"
    : "inline-flex items-center gap-2 rounded-full border-2 border-[var(--ink)] bg-[var(--panini-yellow)] px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-[var(--panini-yellow-soft)] shadow-[3px_3px_0_var(--ink)] transition w-full justify-center";
  return (
    <button
      type="button"
      onClick={doCopy}
      data-testid={testid}
      className={base}
      aria-label="Copy invoice for customer"
    >
      {copied
        ? <><Check size={14} /> Copied!</>
        : err
        ? <><AlertTriangle size={14} /> Retry copy</>
        : <><Copy size={14} /> Copy invoice for customer</>}
    </button>
  );
}


function ZeroResultState({ onBack, unknowns }) {
  return (
    <div className="space-y-4" data-testid="review-zero-state">
      <div className="rounded-2xl border-4 border-[var(--panini-red)] bg-white p-6 md:p-8 shadow-[6px_6px_0_var(--panini-red)]">
        <div className="flex items-start gap-4">
          <div className="shrink-0 mt-1 text-[var(--panini-red)]"><AlertTriangle size={28} /></div>
          <div>
            <div className="kicker kicker-red">Nothing to quote</div>
            <h2 className="h-display text-2xl md:text-3xl mt-1">No valid sticker codes were recognized.</h2>
            <p className="text-sm text-[var(--smoke)] mt-3 leading-relaxed max-w-xl">
              Supported formats include lists copied from <strong>Collector 26</strong>, the
              <strong> Figuritas App</strong>, <strong>StickerCollector</strong>, or a
              simple <code className="font-mono text-[var(--ink)]">Country: Number</code>
              &nbsp;list (e.g. <code className="font-mono text-[var(--ink)]">MEX: 4, 6, 10</code>).
              Flag emojis, app headings, and download URLs are ignored automatically.
            </p>
            <div className="mt-4">
              <button onClick={onBack} className="btn-red text-sm" data-testid="zero-state-back">
                <ArrowLeft size={14} /> Edit your list
              </button>
            </div>
            {unknowns.length > 0 && (
              <div className="mt-6 border-t border-[var(--stone)] pt-4" data-testid="zero-state-unknowns">
                <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">
                  Tokens that could not be understood
                </div>
                <div className="mt-2 flex flex-wrap gap-2 text-xs">
                  {unknowns.slice(0, 20).map((u) => (
                    <span key={`unknown-${u.raw_token}`} className="border-2 border-[var(--stone)] bg-white rounded-lg px-2 py-1 font-mono">
                      <b>{u.raw_token}</b>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


function StickySummary({ totals, onNext, disabled, disabledMessage, invoiceText, selectionBusy, adminAdjustments }) {
  // Admin Quote Adjustments · display-time overlay. When the admin
  // finalized a discount/custom-items adjustment on this quote the
  // frozen `revised_totals` is the authoritative override. Original
  // subtotal + line prices are preserved above; we simply show the
  // discount line and the adjusted grand total.
  const adj = adminAdjustments || null;
  const rev = adj?.revised_totals || null;
  const hasAdminDiscount = Boolean(rev && Number(rev.discount_amount || 0) > 0);
  const discountLabel = adj?.discount?.type === "percentage"
    ? `Admin Discount (${Number(adj.discount.value || 0)}%)`
    : "Admin Discount";
  const grandTotal = rev ? Number(rev.final_total || 0) : Number(totals.total || 0);
  return (
    <aside className="hidden lg:block" data-testid="sticky-summary">
      <div className="sticky top-4 rounded-2xl border-4 border-[var(--ink)] bg-white p-5 shadow-[6px_6px_0_var(--panini-red)]">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)] flex items-center gap-2">
          Your quote
          {selectionBusy && (
            <span className="inline-flex items-center gap-1 text-[9px] text-[var(--smoke)] normal-case" data-testid="sticky-updating">
              <Loader2 size={10} className="animate-spin" /> Updating…
            </span>
          )}
        </div>
        <div className="mt-2 space-y-1.5 text-sm">
          <Row label="Total stickers" value={totals.sticker_count} testid="sticky-count" isCount />
          <Row label="Subtotal" value={totals.subtotal} testid="sticky-subtotal" />
          {hasAdminDiscount && (
            <>
              <Row
                label={discountLabel}
                value={-Number(rev.discount_amount || 0)}
                testid="sticky-admin-discount"
                negative
              />
              <Row
                label="Discounted subtotal"
                value={rev.merch_after_discount}
                testid="sticky-discounted-subtotal"
              />
            </>
          )}
          {totals.shipping?.cost > 0 && (
            <Row label={`Shipping — ${totals.shipping.label}`} value={totals.shipping.cost} testid="sticky-shipping" />
          )}
          {hasAdminDiscount
            ? (Number(rev.tax_amount || 0) > 0 && (
                <Row
                  label={`Sales Tax (${((rev.tax_rate || 0) * 100).toFixed(2)}%)`}
                  value={rev.tax_amount}
                  testid="sticky-tax"
                />
              ))
            : (totals.tax && totals.tax.amount > 0 && (
                <Row label={`Sales Tax (${(totals.tax.rate * 100).toFixed(2)}%)`} value={totals.tax.amount} testid="sticky-tax" />
              ))}
        </div>
        <div className="mt-4 pt-4 border-t-2 border-[var(--ink)]">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">Grand Total</div>
          <div className="font-display text-3xl text-[var(--panini-red)] mt-1" data-testid="sticky-grand-total">
            ${Number(grandTotal || 0).toFixed(2)}
          </div>
        </div>
        <button
          onClick={onNext}
          disabled={disabled}
          className="btn-red text-sm mt-4 w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="sticky-continue"
        >
          Continue to Checkout <ArrowRight size={14} />
        </button>
        {disabled && disabledMessage && (
          <div className="mt-2 text-[11px] text-red-800 bg-red-50 border-2 border-red-200 rounded-lg p-2" data-testid="sticky-disabled-message">
            {disabledMessage}
          </div>
        )}
        {invoiceText && (
          <div className="mt-3 pt-3 border-t-2 border-dashed border-[var(--ink)]/30">
            <CopyInvoiceButton text={invoiceText} testid="sticky-copy-invoice" />
            <div className="mt-2 text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)] text-center">
              DM-ready · Instagram, WhatsApp, Email
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}


function MobileStickyBar({ totals, onNext, disabled, disabledMessage, invoiceText, selectionBusy, adminAdjustments }) {
  const rev = adminAdjustments?.revised_totals || null;
  const grandTotal = rev ? Number(rev.final_total || 0) : Number(totals.total || 0);
  return (
    <div
      className="lg:hidden fixed bottom-0 left-0 right-0 z-40 border-t-4 border-[var(--ink)] bg-white shadow-[0_-4px_20px_rgba(0,0,0,0.08)] overflow-x-clip"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      data-testid="mobile-sticky-bar"
    >
      {disabled && disabledMessage && (
        <div className="px-4 py-1.5 text-[11px] text-red-900 bg-red-50 border-b-2 border-red-200 text-center font-bold" data-testid="mobile-disabled-message">
          {disabledMessage}
        </div>
      )}
      {invoiceText && (
        <div className="px-4 pt-2 pb-1 border-b border-[var(--ink)]/10">
          <CopyInvoiceButton text={invoiceText} testid="mobile-copy-invoice" variant="ghost" />
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)] truncate flex items-center gap-1">
            {totals.sticker_count || 0} stickers
            {selectionBusy && (
              <Loader2 size={10} className="animate-spin" data-testid="mobile-updating" />
            )}
          </div>
          <div className="font-display text-lg sm:text-xl text-[var(--panini-red)] whitespace-nowrap" data-testid="mobile-grand-total">
            ${Number(grandTotal || 0).toFixed(2)}
          </div>
        </div>
        <button onClick={onNext} disabled={disabled} className="btn-red text-sm shrink-0 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="mobile-continue">
          Checkout <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}

function Row({ label, value, testid, isCount, negative }) {
  const n = Number(value || 0);
  let display;
  if (isCount) {
    display = `${n}`;
  } else if (n < 0) {
    display = `− $${Math.abs(n).toFixed(2)}`;
  } else {
    display = `$${n.toFixed(2)}`;
  }
  return (
    <div className="flex items-center justify-between" data-testid={testid}>
      <span className="text-[var(--smoke)]">{label}</span>
      <span className={"font-mono tabular-nums" + (negative ? " text-red-800" : "")}>
        {display}
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  Step 3 — Customer Details + Delivery + Submit
// ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
//  Step 3 — Choose how to check out (guest OR create account)
// ─────────────────────────────────────────────────────────────
function StepChoice({ onGuest, onCreateAccount, onBack }) {
  return (
    <div className="space-y-5" data-testid="choice-step">
      <div className="text-center max-w-2xl mx-auto">
        <div className="kicker kicker-red">Step 3</div>
        <h2 className="h-display text-2xl md:text-3xl mt-2">How would you like to continue?</h2>
        <p className="text-sm text-[var(--smoke)] mt-2">
          Choose the option that works best for you. Both paths take you to secure Zelle checkout.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto">
        {/* Guest card */}
        <div className="rounded-2xl border-4 border-[var(--ink)] bg-white p-6 flex flex-col shadow-[6px_6px_0_var(--stone)]" data-testid="choice-guest-card">
          <div className="flex items-center gap-2">
            <Zap size={22} className="text-[var(--panini-yellow)]" />
            <h3 className="font-display text-xl md:text-2xl">Continue as Guest</h3>
          </div>
          <p className="text-sm text-[var(--smoke)] mt-3 leading-relaxed flex-1">
            Fastest checkout. You&apos;ll receive your secure order tracking link by email.
          </p>
          <button
            onClick={onGuest}
            className="btn-ghost text-sm mt-6 justify-center border-2 border-[var(--ink)]"
            data-testid="choice-guest-btn"
          >
            Continue as Guest <ArrowRight size={14} />
          </button>
        </div>

        {/* Account card */}
        <div className="rounded-2xl border-4 border-[var(--panini-red)] bg-[var(--panini-yellow-soft)] p-6 flex flex-col shadow-[6px_6px_0_var(--panini-red)] relative" data-testid="choice-account-card">
          <div className="absolute -top-3 right-4 rounded-full bg-[var(--panini-red)] text-white text-[10px] uppercase tracking-widest font-bold px-3 py-1">
            Recommended
          </div>
          <div className="flex items-center gap-2">
            <Star size={22} className="text-[var(--panini-red)]" />
            <h3 className="font-display text-xl md:text-2xl">Create a Free CollectorUp Account</h3>
          </div>
          <ul className="text-sm text-[var(--ink)] mt-3 space-y-1.5 flex-1">
            {[
              "View all current and future orders",
              "Access orders from any device",
              "Save your contact information",
              "Faster checkout in the future",
              "Unlock future CollectorUp features",
            ].map((b) => (
              <li key={b} className="flex items-start gap-2">
                <CheckCircle2 size={14} className="text-[var(--panini-red)] mt-0.5 shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
          <button
            onClick={onCreateAccount}
            className="btn-red text-sm mt-5 justify-center"
            data-testid="choice-account-btn"
          >
            <UserPlus size={14} /> Create Free Account
          </button>
        </div>
      </div>

      <div className="text-center pt-2">
        <button onClick={onBack} className="btn-ghost text-sm" data-testid="choice-back">
          <ArrowLeft size={14} /> Back to quote
        </button>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
//  Step 4 — Checkout details (guest form OR guest + account creation)
// ─────────────────────────────────────────────────────────────
function StepCheckout({ quote, customer, setCustomer, delivery, onChangeDelivery, onBack, onSubmit, busy, mode }) {
  const totals = quote.totals || {};
  const set = (k) => (v) => setCustomer((c) => ({ ...c, [k]: v }));
  // Phase 3A.1 — nested address setter.
  const setAddr = (k) => (v) => setCustomer((c) => ({
    ...c,
    shipping_address: { ...(c.shipping_address || {}), [k]: v },
  }));
  const isShipping = delivery !== "local_pickup";
  const addr = customer.shipping_address || {};

  // Account-creation-only form state.
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [phoneError, setPhoneError] = React.useState("");
  // Shipping & Fulfillment · Phase 1 — USPS Stamp risk acknowledgment.
  // Required only when the customer selects USPS Stamp. Reset when the
  // customer switches away (handled by useEffect below).
  const [uspsAck, setUspsAck] = React.useState(false);
  const isAccountMode = mode === "account";
  const needsUspsAck = delivery === "usps_stamp";
  const uspsAckSatisfied = !needsUspsAck || uspsAck;

  React.useEffect(() => {
    if (delivery !== "usps_stamp" && uspsAck) setUspsAck(false);
  }, [delivery, uspsAck]);

  const handleSubmit = () => {
    setPhoneError("");
    if (!customer.phone.trim()) {
      setPhoneError("Phone number is required — customers need it for pickup or delivery updates.");
      toast.error("Phone number is required.");
      return;
    }
    if (needsUspsAck && !uspsAck) {
      toast.error("Please acknowledge the USPS Stamp no-tracking risk before continuing.");
      return;
    }
    if (isAccountMode) {
      if (!firstName.trim() || !lastName.trim()) {
        return toast.error("Enter both first and last name.");
      }
      if (password.length < 10) return toast.error("Password must be at least 10 characters.");
      if (password !== confirm) return toast.error("Passwords do not match.");
      // Sync the customer name for the order.
      const fullName = `${firstName.trim()} ${lastName.trim()}`;
      setCustomer((c) => ({ ...c, name: fullName }));
      onSubmit({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: customer.email,
        phone: customer.phone,
        password,
        confirm_password: confirm,
        usps_acknowledgment: uspsAck,
      });
    } else {
      onSubmit({ usps_acknowledgment: uspsAck });
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="checkout-step">
      <div className="lg:col-span-2 space-y-5">
        {/* Delivery method */}
        <div className="rounded-2xl border-4 border-[var(--ink)] bg-white p-5 md:p-6">
          <div className="kicker kicker-red">Delivery</div>
          <h3 className="font-display text-xl md:text-2xl mt-1.5">How should we get your stickers to you?</h3>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="delivery-options">
            {DELIVERY_OPTIONS.map((d) => (
              <button
                key={d.code}
                onClick={() => onChangeDelivery(d.code)}
                disabled={busy}
                className={`text-left p-4 border-2 rounded-xl transition ${
                  delivery === d.code
                    ? "border-[var(--panini-red)] bg-[var(--panini-yellow-soft)] shadow-[4px_4px_0_var(--panini-red)]"
                    : "border-[var(--ink)] bg-white hover:bg-[var(--panini-yellow-soft)]"
                }`}
                data-testid={`delivery-${d.code}`}
              >
                <div className="flex items-center gap-2 font-bold text-sm">
                  {d.code === "local_pickup" ? <MapPin size={14} /> : <Truck size={14} />}
                  {d.label}
                </div>
                <div className="mt-2 text-xs text-[var(--smoke)]">{d.hint}</div>
                <div className="mt-3 font-mono font-bold text-base">
                  {Number(d.cost) === 0 ? "Free" : `$${Number(d.cost).toFixed(2)}`}
                </div>
                {d.code === "usps_stamp" && (
                  <div className="mt-2 text-[10px] uppercase tracking-widest font-bold text-[var(--panini-red)]"
                       data-testid="delivery-usps-notrack-tag">
                    No tracking number
                  </div>
                )}
              </button>
            ))}
          </div>
          {delivery === "usps_stamp" && (
            <div className="mt-4 rounded-xl border-2 border-[var(--panini-red)] bg-red-50 p-4"
                 data-testid="usps-ack-panel">
              <div className="text-sm font-bold text-[var(--panini-red)]">
                No tracking number
              </div>
              <p className="mt-1 text-xs text-[var(--ink)] leading-relaxed">
                This shipping method does not include tracking. CollectorUp cannot be held responsible if the envelope is lost, delayed, or does not arrive. Choose this option at your own risk.
              </p>
              <label className="mt-3 flex items-start gap-2 text-xs text-[var(--ink)] cursor-pointer">
                <input
                  type="checkbox"
                  checked={uspsAck}
                  onChange={(e) => setUspsAck(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-[var(--panini-red)]"
                  data-testid="usps-ack-checkbox"
                />
                <span>
                  I understand that USPS Stamp shipping has no tracking and I accept the risk of loss or non-delivery.
                </span>
              </label>
            </div>
          )}
        </div>

        {/* Account creation (only in account mode) */}
        {isAccountMode && (
          <div className="rounded-2xl border-4 border-[var(--panini-red)] bg-[var(--panini-yellow-soft)] p-5 md:p-6" data-testid="account-form">
            <div className="kicker kicker-red">Create your CollectorUp account</div>
            <p className="text-xs text-[var(--smoke)] mt-1">
              You&apos;ll be signed in immediately. No email verification needed to place this order.
            </p>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <TextInput label="First name *" testid="acct-first" value={firstName} onChange={setFirstName} />
              <TextInput label="Last name *"  testid="acct-last"  value={lastName}  onChange={setLastName} />
              <TextInput label="Password (min 10) *"       testid="acct-password" value={password} onChange={setPassword} type="password" />
              <TextInput label="Confirm password *"        testid="acct-confirm"  value={confirm}  onChange={setConfirm}  type="password" />
            </div>
          </div>
        )}

        {/* Contact details */}
        <div className="rounded-2xl border-4 border-[var(--ink)] bg-white p-5 md:p-6">
          <div className="kicker kicker-red">Your Details</div>
          <h3 className="font-display text-xl md:text-2xl mt-1.5">
            {isAccountMode ? "Contact info for your order" : "Where should we send your quote receipt?"}
          </h3>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            {!isAccountMode && (
              <>
                {/* Missing-Sticker Ordering · Customer Identity Gate —
                    split full name into two explicit inputs so the
                    backend receives structured identity fields and
                    downstream pages can render the customer's real
                    first + last name. */}
                <TextInput label="First name *" testid="cust-first-name"
                              value={customer.first_name || ""}
                              onChange={set("first_name")} />
                <TextInput label="Last name *"  testid="cust-last-name"
                              value={customer.last_name || ""}
                              onChange={set("last_name")} />
              </>
            )}
            <TextInput label="Email *"        testid="cust-email" value={customer.email} onChange={set("email")} type="email" />
            <div>
              <TextInput label="Phone number *" testid="cust-phone" value={customer.phone} onChange={(v) => { set("phone")(v); if (v.trim()) setPhoneError(""); }} placeholder="+1 555 …" />
              {phoneError && (
                <div className="mt-1 text-xs text-[var(--panini-red)] font-bold" role="alert" data-testid="cust-phone-error">
                  {phoneError}
                </div>
              )}
            </div>
            <TextInput label="Instagram (optional)" testid="cust-ig" value={customer.instagram_handle} onChange={set("instagram_handle")} placeholder="@yourhandle" />
          </div>
          <label className="block mt-4" data-testid="cust-pinned-field">
            <span className="text-[10px] uppercase tracking-widest font-bold">Order notes (optional)</span>
            <textarea
              rows={3}
              maxLength={500}
              value={customer.pinned_instructions}
              onChange={(e) => set("pinned_instructions")(e.target.value)}
              className="w-full mt-1 border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm"
              placeholder="e.g. Please pack in a rigid mailer — traveling until Friday."
              data-testid="cust-pinned"
            />
          </label>
        </div>

        {/* Phase 3A.1 — Shipping address (only for non-pickup delivery) */}
        {isShipping && (
          <div className="rounded-2xl border-4 border-[var(--ink)] bg-white p-5 md:p-6" data-testid="shipping-address-card">
            <div className="kicker kicker-red">Ship to</div>
            <h3 className="font-display text-xl md:text-2xl mt-1.5">Where should we ship your stickers?</h3>
            <p className="text-xs text-[var(--smoke)] mt-1">
              We&apos;ll use this exact address on the shipping label. Apartment / suite goes on line 2.
            </p>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <TextInput label="Address line 1 *" testid="ship-line1" value={addr.line1 || ""} onChange={setAddr("line1")} placeholder="123 Main Street" />
              </div>
              <div className="md:col-span-2">
                <TextInput label="Apartment / suite / unit (optional)" testid="ship-line2" value={addr.line2 || ""} onChange={setAddr("line2")} placeholder="Apt 2B" />
              </div>
              <TextInput label="City *"       testid="ship-city"       value={addr.city || ""}       onChange={setAddr("city")}       placeholder="Miami" />
              <TextInput label="State / Region *" testid="ship-state"  value={addr.state || ""}      onChange={setAddr("state")}      placeholder="FL" />
              <TextInput label="ZIP / Postal code *" testid="ship-zip" value={addr.postal_code || ""} onChange={setAddr("postal_code")} placeholder="33169" />
              <TextInput label="Country *"    testid="ship-country"    value={addr.country || "United States"} onChange={setAddr("country")} placeholder="United States" />
            </div>
          </div>
        )}
      </div>

      {/* Sidebar summary */}
      <div className="space-y-4">
        <div className="rounded-2xl border-4 border-[var(--ink)] bg-white p-5 md:p-6 sticky top-4 shadow-[6px_6px_0_var(--panini-red)]" data-testid="checkout-quote">
          <div className="text-[10px] uppercase tracking-widest font-bold">Your quote</div>
          <div className="mt-2 space-y-1 text-sm">
            <Row label="Stickers subtotal" value={totals.subtotal} testid="side-subtotal" />
            {totals.tax && totals.tax.amount > 0 && <Row label={`Sales tax (${(totals.tax.rate * 100).toFixed(2)}%)`} value={totals.tax.amount} testid="side-tax" />}
            {totals.shipping?.cost > 0 && <Row label={`Shipping — ${totals.shipping.label}`} value={totals.shipping.cost} testid="side-ship" />}
          </div>
          <div className="mt-3 pt-3 border-t-2 border-[var(--ink)] flex items-center justify-between">
            <span className="font-bold">Total</span>
            <span className="font-display text-2xl" data-testid="checkout-total">
              ${Number(totals.total || 0).toFixed(2)}
            </span>
          </div>
          <p className="text-[11px] text-[var(--smoke)] mt-3 italic">
            Pay by Zelle on the next page. You&apos;ll receive an emailed receipt with your order ID.
          </p>
        </div>
        <div className="flex items-center justify-between gap-3">
          <button onClick={onBack} className="btn-ghost text-sm" data-testid="checkout-back">
            <ArrowLeft size={14} /> Back
          </button>
          <button
            onClick={handleSubmit}
            disabled={busy || !uspsAckSatisfied}
            className="btn-red text-base"
            data-testid="checkout-submit"
          >
            {busy ? <><Loader2 size={16} className="animate-spin" /> Submitting…</>
                  : <><ShieldCheck size={16} /> {isAccountMode ? "Create Account & Submit" : "Submit Order"}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function TextInput({ label, testid, value, onChange, type = "text", placeholder }) {
  return (
    <label className="block" data-testid={`${testid}-field`}>
      <span className="text-[10px] uppercase tracking-widest font-bold">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full mt-1 border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm"
        data-testid={testid}
      />
    </label>
  );
}

// ─────────────────────────────────────────────────────────────
//  Optional help footer (subordinate to self-serve flow)
// ─────────────────────────────────────────────────────────────
function ContactFooter() {
  return (
    <div className="mt-12 pt-8 border-t-2 border-[var(--stone)]" data-testid="missing-contact-footer">
      <div className="max-w-2xl">
        <div className="kicker kicker-red">Need help?</div>
        <h3 className="font-display text-xl md:text-2xl mt-2">Have questions? Contact us.</h3>
        <p className="text-sm text-[var(--smoke)] mt-2">
          The website should complete your quote without help — but if something looks off or you get stuck, we&apos;re here.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <a
            href="https://instagram.com/gilmaakaroun"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--ink)] bg-white px-4 py-2 text-sm font-bold hover:bg-[var(--panini-yellow-soft)]"
            data-testid="contact-instagram"
          >
            <Instagram size={14} /> Instagram DM
          </a>
          <a
            href="https://wa.me/17865756919"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--ink)] bg-white px-4 py-2 text-sm font-bold hover:bg-[var(--panini-yellow-soft)]"
            data-testid="contact-whatsapp"
          >
            <MessageCircle size={14} /> WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
}
